import { useEffect, useMemo, useRef, useState } from "react";
import Header from "../Header/Header";
import Footer from "../Footer";
import { useLanguage } from "../contexts/LanguageContext";
import { img } from "../utils/cloudinaryImage";
import s from "./ItineraryPlannerPage.module.scss";

const mapSearchUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addLocalDays = (dateKey, amount) => {
  const date = new Date(`${dateKey}T12:00:00`);
  date.setDate(date.getDate() + amount);
  return localDateKey(date);
};

const weatherIcon = (code) => {
  if (code == null) return "--";
  if (code >= 95) return "⛈️";
  if (code >= 61) return "🌧️";
  if (code >= 51) return "🌦️";
  if (code >= 45) return "🌫️";
  if (code >= 3) return "☁️";
  if (code >= 1) return "🌤️";
  return "☀️";
};

const numbersForDate = (times = [], values = [], dateKey, startHour = 6, endHour = 18) =>
  times.reduce((result, time, index) => {
    if (!String(time).startsWith(dateKey)) return result;
    const hour = Number(String(time).slice(11, 13));
    if (hour >= startHour && hour <= endHour && Number.isFinite(Number(values[index]))) {
      result.push(Number(values[index]));
    }
    return result;
  }, []);

const buildTripForecast = (cache, startDate, dayCount) => {
  if (!cache?.weather?.daily) return [];
  const weatherDaily = cache.weather.daily;
  const windHourly = cache.wind?.hourly || cache.weather?.hourly || {};
  const marineHourly = cache.marine?.hourly || {};

  return Array.from({ length: dayCount }, (_, index) => {
    const date = addLocalDays(startDate, index);
    const dailyIndex = (weatherDaily.time || []).indexOf(date);
    if (dailyIndex < 0) return { date, available: false };

    const rainValues = numbersForDate(
      cache.weather.hourly?.time,
      cache.weather.hourly?.precipitation_probability,
      date,
    );
    const windValues = numbersForDate(
      windHourly.time,
      windHourly.wind_speed_10m,
      date,
    );
    const waveValues = numbersForDate(
      marineHourly.time,
      marineHourly.wave_height,
      date,
    );
    const rain = rainValues.length
      ? Math.round(rainValues.reduce((sum, value) => sum + value, 0) / rainValues.length)
      : Number(weatherDaily.precipitation_probability_max?.[dailyIndex] || 0);
    const wind = windValues.length ? Math.max(...windValues) : 0;
    const wave = waveValues.length ? Math.max(...waveValues) : 0;
    const level = rain >= 60 || wind >= 7 || wave >= 1.5
      ? "caution"
      : rain >= 40 || wind >= 5 || wave >= 1
        ? "watch"
        : "good";

    return {
      date,
      available: true,
      code: weatherDaily.weather_code?.[dailyIndex],
      high: Math.round(weatherDaily.temperature_2m_max?.[dailyIndex]),
      low: Math.round(weatherDaily.temperature_2m_min?.[dailyIndex]),
      rain,
      wind,
      wave,
      level,
    };
  });
};

const tripWeatherText = (forecast, lang) => {
  if (!forecast?.available) {
    return lang === "JP" ? "予報期間外です。日程が近づいたらOcean Guideで確認してください。" : "Forecast data is not available yet.";
  }
  const messages = {
    good: {
      JP: "海遊びを入れやすい日です。午前中を中心に予定を組むのがおすすめです。",
      EN: "A good day for ocean activities. Plan them mainly in the morning.",
      中文: "适合安排海上活动，建议以上午为主。",
      한국어: "해양 액티비티를 넣기 좋은 날입니다. 오전 중심으로 계획해 보세요.",
    },
    watch: {
      JP: "天気・風・波の変化を見ながら、海と陸の予定を入れ替えられるプランがおすすめです。",
      EN: "Keep the plan flexible so ocean and land activities can be swapped.",
      中文: "建议保留弹性，可根据天气在海上与陆上行程之间调整。",
      한국어: "날씨에 따라 바다와 육상 일정을 바꿀 수 있게 여유롭게 계획하세요.",
    },
    caution: {
      JP: "海況に注意が必要です。カフェ・観光・室内体験を中心にし、海は当日のOcean Guideを確認してください。",
      EN: "Ocean conditions need caution. Prioritize cafes and indoor sightseeing, then check Ocean Guide that day.",
      中文: "海况需要注意。建议以咖啡店和室内观光为主，当天再查看Ocean Guide。",
      한국어: "해황에 주의가 필요합니다. 카페와 실내 관광을 중심으로 하고 당일 Ocean Guide를 확인하세요.",
    },
  };
  return messages[forecast.level]?.[lang] || messages[forecast.level]?.JP;
};

const fetchLiveTripOceanData = async () => {
  const common = "latitude=24.82&longitude=125.265&timezone=Asia%2FTokyo&forecast_days=7";
  const [weatherResponse, windResponse, marineResponse] = await Promise.all([
    fetch(`https://api.open-meteo.com/v1/forecast?${common}&hourly=precipitation_probability%2Cweather_code%2Ctemperature_2m%2Cwind_speed_10m&daily=weather_code%2Ctemperature_2m_max%2Ctemperature_2m_min%2Cprecipitation_probability_max&wind_speed_unit=ms`),
    fetch(`https://api.open-meteo.com/v1/forecast?${common}&hourly=wind_speed_10m%2Cwind_direction_10m%2Cwind_gusts_10m&wind_speed_unit=ms`),
    fetch(`https://marine-api.open-meteo.com/v1/marine?${common}&hourly=wave_height%2Cswell_wave_height&daily=wave_height_max%2Cswell_wave_height_max`),
  ]);
  if (![weatherResponse, windResponse, marineResponse].every((response) => response.ok)) {
    throw new Error("Live trip forecast fetch failed");
  }
  const [weather, wind, marine] = await Promise.all([
    weatherResponse.json(),
    windResponse.json(),
    marineResponse.json(),
  ]);
  return { fetchedAt: new Date().toISOString(), weather, wind, marine };
};

const hasSpecificMapLocation = (item) => {
  const query = item?.mapQuery?.trim();
  if (!query) return false;

  return (
    !/^(宮古島|Miyakojima|宫古岛)\s/i.test(query) &&
    query !== "池間島 カフェ"
  );
};

const instagramPosts = {
  "みやこ下地島空港": "https://www.instagram.com/reel/Da_kmcGzbu-/embed",
  "51レンタカー下地店": "https://www.instagram.com/p/DTXtMNIgRh_/embed",
  "Blue Turtle": "https://www.instagram.com/p/DTZdliiE1Xt/embed",
  "17END": "https://www.instagram.com/p/CwHkRa4r-hh/embed",
  "朝ごはん": "https://www.instagram.com/p/DVxod2nk23q/embed",
  "ユニの浜ツアー": "https://www.instagram.com/reel/DbiAqVRxhSl/embed",
  "おすすめの夜ご飯屋": "https://www.instagram.com/p/DbqHRifzEaA/embed",
  "Pani Pani": "https://www.instagram.com/reel/CwP45f7KSse/embed",
  "島茶家ヤッカヤッカ": "https://www.instagram.com/reel/ClqeHlmjGq_/embed",
  "SHISA'S CAFE & BBQ": "https://www.instagram.com/reel/Db2pn2XTso5/embed",
  "國仲商店": "https://www.instagram.com/p/C2HlvxDP7Ir/embed",
  "島ごはんランチ": "https://www.instagram.com/p/DLEUnxnxJ9D/embed",
  "池間大橋で撮影": "https://www.instagram.com/reel/DWgKV-3kQCS/embed",
  "ハート岩＆イキヅービーチ": "https://www.instagram.com/reel/DbfuIP2tDsu/embed",
  "フナクスビーチ": "https://www.instagram.com/reel/C6pNMSoSgSf/embed",
  "海美来": "https://www.instagram.com/p/Ck_Dl_kytYp/embed",
  "Gelato cafe Ninufa": "https://www.instagram.com/reel/DYEeG9rz_Ks/embed",
  "OHAMAテラス": "https://www.instagram.com/p/DGRlEe7z8d5/embed",
  "八重干瀬ツアー": "https://www.instagram.com/reel/DYzOD5cS4nj/embed",
  "ホテルでゆっくり夜ご飯": "https://www.instagram.com/reel/DSHz4p9khQl/embed",
  "雨の日の室内体験": "https://www.instagram.com/reel/DYCClqNJIqT/embed",
};

const localTourVideos = {
  "絶景撮影ツアー": "/media/photo-tour.mp4",
  "秘境SUPツアー": "/media/sup-tour.mp4",
};

const cafeInstagram = [
  ["Seahorse Club Cafe", "@seahorseclub385", "https://www.instagram.com/seahorseclub385/"],
  ["OHAMAテラス", "@ohamaterasu", "https://www.instagram.com/ohamaterasu/"],
  ["Pani Pani", "Instagram Reel", "https://www.instagram.com/reel/CwP45f7KSse/"],
  ["ブルーシール パイナガマ店", "@blueseal_painagama", "https://www.instagram.com/blueseal_painagama/"],
  ["島茶家ヤッカヤッカ", "Instagram Reel", "https://www.instagram.com/reel/ClqeHlmjGq_/"],
  ["SHISA'S CAFE & BBQ", "@shisas_cafe_bbq", "https://www.instagram.com/shisas_cafe_bbq/"],
  ["HARRY'S Shrimp Truck", "@harrysshrimptruck", "https://www.instagram.com/harrysshrimptruck/"],
];

const dinnerInstagram = [
  ["島おでん たから", "Google Map", mapSearchUrl("島おでん たから 宮古島"), "0980-72-0671"],
  ["美ら島", "Google Map", mapSearchUrl("美ら島 宮古島"), "0980-79-9788"],
  ["居酒家 でいりぐち", "Google Map", mapSearchUrl("居酒家 でいりぐち 宮古島"), "0980-72-8017"],
  ["うまりずま", "Google Map", mapSearchUrl("うまりずま 宮古島"), "0980-79-0335"],
  ["焼肉 喜八", "Google Map", mapSearchUrl("焼肉 喜八 宮古島"), "0980-73-3859"],
  ["しゃぶしゃぶ 嘉福", "Google Map", mapSearchUrl("しゃぶしゃぶ 嘉福 宮古島"), "0980-79-6575"],
];

const lunchInstagram = [
  ["Spice & Fresh", "spiceCurry", mapSearchUrl("Spice & Fresh 宮古島"), "070-3305-6779"],
  ["Moses Curry", "curry", mapSearchUrl("Moses Curry 宮古島"), "070-4416-8874"],
  ["デンみ", "localDiner", mapSearchUrl("デンみ 宮古島"), "0980-78-3535"],
  ["カフェ ウエスヤ", "cafeMeal", mapSearchUrl("カフェ ウエスヤ 宮古島"), "0980-73-5286"],
  ["島茶家ヤッカヤッカ", "miyakoBeefCurry", mapSearchUrl("島茶家ヤッカヤッカ 宮古島"), "0980-74-7205"],
  ["麺屋サマー太陽", "ramen", mapSearchUrl("麺屋サマー太陽 宮古島"), "0980-79-0597"],
  ["焼肉 牛平", "Google Map", mapSearchUrl("焼肉 牛平 宮古島"), "0980-78-5030"],
];

const hotelBookingUrl =
  "https://www.booking.com/searchresults.ja.html?aid=311100&label=hoterugong-gu-dao-Ez7_NjErGmD5b2_RAMon9wS477169139896%3Apl%3Ata%3Ap1%3Ap2%3Aac%3Aap%3Aneg%3Afi%3Atiaud-2382347442848%3Akwd-2508008730%3Alp9174342%3Ali%3Adec%3Adm%3Appccp%3DUmFuZG9tSVYkc2RlIyh9Ybu3g3JQEN-yGShcLON2_Wc&gclid=Cj0KCQjwv4XUBhDBARIsAE6bQUT83VKIuqc2G8BhiG-M5ys1MTPAPIdqbV7sTCE59k0EqriqBLjWRYwaApmJEALw_wcB&redirected=1&city=900050296&highlighted_hotels=4341765&hlrd=no_dates&source=hotel&expand_sb=1&keep_landing=1&sid=22f221f94f9f6b458244171000bc91ee";

const hotelDinnerOptions = [
  ["キャノピー by ヒルトン沖縄宮古島リゾート", "Google Map", "https://maps.app.goo.gl/jATFA9NnE6UrjyKx9"],
  ["ヒルトン沖縄宮古島リゾート", "Google Map", "https://maps.app.goo.gl/xi3E1gSHGFG56KW77"],
  ["宮古島東急ホテル＆リゾーツ", "Google Map", "https://maps.app.goo.gl/vCrCqpFNTifN5sb2A"],
  ["宮古島来間リゾート シーウッドホテル", "Google Map", "https://maps.app.goo.gl/dw8kp2jnnCG7Rygt6"],
  ["ローズウッド宮古島", "Google Map", "https://maps.app.goo.gl/dxmFuVZamR7sUWLK6"],
  ["ホテルシギラミラージュ", "Google Map", "https://maps.app.goo.gl/Qi6MiYfo7cZMM3tLA"],
  ["イラフ SUI ラグジュアリーコレクションホテル 沖縄宮古", "Google Map", "https://maps.app.goo.gl/idtw2EExMeiy2obo8"],
];

const breakfastOptions = [
  {
    category: "cafe",
    title: "朝ごはん",
    detail: "くじら食堂で朝ごはんをテイクアウト。",
    mapQuery: "くじら食堂 宮古島",
  },
  {
    category: "cafe",
    title: "國仲商店",
    detail: "國仲商店でモーニングとコーヒーを楽しみます。",
    mapQuery: "國仲商店 宮古島",
  },
  {
    category: "cafe",
    title: "PAINAGAMA BLUE BOOTH",
    detail: "宮古島食材を使ったホットドッグをテイクアウト。",
    mapQuery: "PAINAGAMA BLUE BOOTH 宮古島",
    instagramUrl: "https://www.instagram.com/miyako_pbb/",
    websiteUrl: "https://tabelog.com/okinawa/A4705/A470503/47022516/",
    websiteLabelKey: "storeInfo",
  },
];

const breakfastTranslations = {
  EN: [
    { title: "Breakfast", detail: "Pick up a takeaway breakfast from Kujira Shokudo." },
    { title: "Kuninaka Shoten", detail: "Enjoy a morning set and coffee at Kuninaka Shoten." },
    { title: "PAINAGAMA BLUE BOOTH", detail: "Pick up a hot dog made with Miyakojima ingredients." },
  ],
  中文: [
    { title: "早餐", detail: "在Kujira Shokudo外带早餐。" },
    { title: "國仲商店", detail: "在國仲商店享用早餐套餐和咖啡。" },
    { title: "PAINAGAMA BLUE BOOTH", detail: "外带使用宫古岛食材制作的热狗。" },
  ],
  한국어: [
    { title: "아침 식사", detail: "쿠지라 식당에서 아침 식사를 테이크아웃합니다." },
    { title: "쿠니나카 상점", detail: "쿠니나카 상점에서 모닝 세트와 커피를 즐깁니다." },
    { title: "PAINAGAMA BLUE BOOTH", detail: "미야코지마 식재료로 만든 핫도그를 테이크아웃합니다." },
  ],
};

const mapDirectionsUrl = (items) => {
  const places = items
    .filter((item) => hasSpecificMapLocation(item) && item.category !== "tour")
    .map((item) => item.mapQuery);

  if (places.length === 0) return mapSearchUrl("宮古島");
  if (places.length === 1) return mapSearchUrl(places[0]);

  const origin = places[0];
  const destination = places[places.length - 1];
  const waypoints = places.slice(1, -1).join("|");

  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    origin,
  )}&destination=${encodeURIComponent(destination)}${
    waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""
  }&travelmode=driving`;
};

const text = {
  JP: {
    kicker: "Miyako Trip Planner",
    title: "宮古島の旅程を、地図でそのまま巡れる形に",
    lead: "泊数とやりたいことを選ぶだけで、無理なく回れるモデル日程を作れます。作った日程でGoogle Mapが開けます。",
    nights: "何泊する予定？",
    inputModeTitle: "選び方",
    inputModes: {
      interests: "やりたいこと",
      mood: "気分",
    },
    interests: "やりたいこと",
    moods: "旅の気分",
    generate: "この内容で旅程を作る",
    route: "この日のルートをGoogle Mapで開く",
    map: "地図",
    routeShort: "経路",
    allRoute: "全体ルート",
    allRouteText: "旅行全体をGoogle Mapで確認",
    result: "おすすめ日程",
    generatedDone: "旅程を作成しました",
    combinedTheme: "午前・午後・夜に分けて、移動と休憩に余裕を持たせたプランです。",
    arrivalTitle: "宮古島到着",
    arrivalPhotoTitle: "宮古島到着・絶景撮影ツアー",
    arrivalCafeTitle: "宮古島到着・カフェ巡り",
    bookingDefault: "予約プランを見る",
    detailDefault: "ツアー詳細を見る",
    officialSite: "公式サイト",
    storeInfo: "店舗情報",
    videoLabel: "動画",
    mapPreviewTitle: "宮古島 Google Map",
    departureTheme: "チェックアウト後に朝ごはんを受け取り、余裕を持って空港へ向かいます。",
    lunchGenres: { spiceCurry: "スパイスカレー", curry: "カレー", localDiner: "ローカル食堂", cafeMeal: "カフェごはん", miyakoBeefCurry: "宮古牛カレー", ramen: "ラーメン・つけ麺" },
    helper:
      "選択内容に合わせて、撮影・海遊び・カフェ・夜ご飯を詰め込みすぎない順番で組みます。",
    day: "Day",
    saveHint:
      "このページはまずモデル日程として使える形です。今後、入力内容の保存や予約相談への送信も追加できます。",
    labels: {
      arrival: "到着",
      departure: "出発",
      tour: "ツアー",
      cafe: "カフェ",
      lunch: "ランチ",
      dinner: "夜ご飯",
      beach: "ビーチ",
      night: "夜遊び",
      star: "星空",
      rest: "余白",
      rentacar: "レンタカー",
    },
    interestLabels: {
      photo: "絶景撮影",
      sup: "秘境SUP",
      cafe: "映えるカフェ",
      dinner: "夜ご飯",
      night: "夜遊び",
      star: "星空撮影",
      yuni: "ユニの浜",
      yaebiji: "八重干瀬",
      drone: "ドローン撮影",
      drive: "ドライブ",
      rentacar: "レンタカー",
      secretBeach: "シークレットビーチ",
    },
    moodLabels: {
      healing: "癒しの時間を満喫したい",
      packed: "宮古島をしっかり満喫したい",
      shootSlow: "写真・映像をしっかり残したい",
      selfDrive: "レンタカーで自由に巡りたい",
      rainy: "雨で何すればいい？☔️",
    },
    customPlans: {
      hotelDinnerTitle: "ホテルでゆっくり夜ご飯",
      hotelDinnerDetail: "夜は移動を詰め込みすぎず、ホテルやリゾート内でゆっくり過ごします。",
      hotelDinnerNote: "人気のホテルは事前予約がおすすめです。",
      hotelRelaxTitle: "ホテルでゆっくり過ごす",
      hotelRelaxDetail: "雨の日は無理に動かず、ホテルやリゾート内でゆっくり過ごすのもおすすめです。",
      rainyIndoorTitle: "雨の日の室内体験",
      rainyIndoorDetail: "レジンアートやシーサー作りなど、雨の日でも宮古島らしい思い出を形に残せます。",
      rainyIndoorNote: "当日予約できる場合もありますが、Instagramや電話で事前確認がおすすめです。",
      rainyIndoorDetailLabel: "雨の日の過ごし方を見る",
      photogenicCafeTitle: "映えるカフェ",
      photogenicCafeDetail: "海遊びの後は、行きたいカフェを一覧から選んでゆっくり休憩。",
      photogenicCafeNote: "港からの移動やその日の気分に合わせて選べます。",
      rainyCafeTitle: "映えるカフェでのんびり",
      rainyCafeDetail: "雨の日は屋内で過ごしやすいカフェを選んで、ゆっくり休憩。",
      rainyCafeNote: "海に入れない日でも、カフェ時間をうまく使えば旅を楽しめます。",
      hotelBooking: "宮古島のホテルを予約する",
      hotelStarTitle: "ホテルでゆっくり夜ご飯と星空",
      hotelStarTheme: "ホテルでゆっくり夜ご飯を楽しんだあと、静かな場所で星空撮影を行います。",
      shootArrivalTitle: "秘境フォトツアーで写真・映像を残す日",
      shootArrivalTheme: "午後は秘境フォトツアーで、写真と映像をしっかり残します。",
      shootArrivalWithStarTheme: "午後は秘境フォトツアー、夜は星空撮影だけをゆっくり楽しみます。",
      yabijiStarTitle: "八重干瀬と星空を楽しむ日",
      yabijiStarTheme: "午前中は八重干瀬ツアーへ。夕方は映えるカフェ、夜ご飯のあとに星空撮影を楽しみます。",
      rainyArrivalTitle: "宮古島到着・雨の日プラン",
      rainyMainTitle: "雨の日でも楽しめる宮古島",
      rainyMainTheme: "ホテルでゆっくり、カフェでのんびり、室内体験で思い出を残す雨の日プランです。",
      rainyCafeHotelTitle: "雨の日のカフェとホテル時間",
      rainyCafeHotelTheme: "移動を少なめにして、カフェとホテルでゆっくり過ごします。",
      rainyIndoorPlanTitle: "室内体験で旅の思い出作り",
      rainyIndoorPlanTheme: "海に入れない日でも、宮古島らしい体験を楽しめます。",
    },
    nightsOptions: {
      1: "1泊2日",
      2: "2泊3日",
      3: "3泊4日",
      4: "4泊5日",
      5: "5泊6日",
    },
  },
  EN: {
    kicker: "Miyako Trip Planner",
    title: "Build a Miyakojima itinerary you can open in Google Maps.",
    lead: "Choose your stay length and interests, then get a relaxed day-by-day plan. Each stop opens in Google Maps for easy travel on the island.",
    nights: "How many nights?",
    inputModeTitle: "Choose by",
    inputModes: {
      interests: "Things to do",
      mood: "Mood",
    },
    interests: "What do you want to do?",
    moods: "Trip mood",
    generate: "Generate itinerary",
    route: "Open this day in Google Maps",
    map: "Map",
    routeShort: "Route",
    allRoute: "Full Route",
    allRouteText: "Open the whole trip in Google Maps",
    result: "Suggested Schedule",
    generatedDone: "Itinerary generated",
    combinedTheme: "A relaxed plan split between morning, afternoon, and evening with time for travel and breaks.",
    arrivalTitle: "Arrival in Miyakojima",
    arrivalPhotoTitle: "Arrival & Scenic Photo Tour",
    arrivalCafeTitle: "Arrival & Cafe Tour",
    bookingDefault: "View booking options",
    detailDefault: "View tour details",
    officialSite: "Official website",
    storeInfo: "Store information",
    videoLabel: "video",
    mapPreviewTitle: "Miyakojima Google Map",
    departureTheme: "Check out, pick up breakfast, and head to the airport with time to spare.",
    lunchGenres: { spiceCurry: "Spice curry", curry: "Curry", localDiner: "Local diner", cafeMeal: "Cafe meal", miyakoBeefCurry: "Miyako beef curry", ramen: "Ramen & tsukemen" },
    helper:
      "We balance photo tours, ocean activities, cafes, and dinner without overpacking each day.",
    day: "Day",
    saveHint:
      "This page currently works as a model itinerary. Saving and sending the plan for booking can be added next.",
    labels: {
      arrival: "Arrival",
      departure: "Departure",
      tour: "Tour",
      cafe: "Cafe",
      lunch: "Lunch",
      dinner: "Dinner",
      beach: "Beach",
      night: "Night",
      star: "Stars",
      rest: "Slow Time",
      rentacar: "Rental Car",
    },
    interestLabels: {
      photo: "Scenic photo tour",
      sup: "Hidden SUP",
      cafe: "Photogenic cafes",
      dinner: "Local dinner",
      night: "Nightlife",
      star: "Star photo",
      yuni: "Yuni no Hama",
      yaebiji: "Yabiji",
      drone: "Drone shooting",
      drive: "Drive",
      rentacar: "Rental car",
      secretBeach: "Secret beach",
    },
    moodLabels: {
      healing: "Enjoy relaxing healing time",
      packed: "Make the most of Miyakojima",
      shootSlow: "Capture photos and videos properly",
      selfDrive: "Explore freely by rental car",
      rainy: "What to do on a rainy day? ☔️",
    },
    customPlans: {
      hotelDinnerTitle: "Relaxed dinner at the hotel",
      hotelDinnerDetail: "Keep the evening easy and spend time inside your hotel or resort.",
      hotelDinnerNote: "Popular hotels are best booked in advance.",
      hotelRelaxTitle: "Relax at the hotel",
      hotelRelaxDetail: "On a rainy day, it is also nice to slow down and enjoy your hotel or resort.",
      rainyIndoorTitle: "Rainy-day indoor activity",
      rainyIndoorDetail: "Try resin art or shisa painting and turn the rainy day into a Miyakojima memory.",
      rainyIndoorNote: "Same-day bookings may be possible, but checking by Instagram or phone is recommended.",
      rainyIndoorDetailLabel: "View rainy-day ideas",
      photogenicCafeTitle: "Photogenic cafes",
      photogenicCafeDetail: "After the ocean, choose a cafe from the list and take a relaxed break.",
      photogenicCafeNote: "Pick one based on your location and mood that day.",
      rainyCafeTitle: "Relax at a photogenic cafe",
      rainyCafeDetail: "On a rainy day, choose an indoor-friendly cafe and slow down.",
      rainyCafeNote: "Even without ocean time, cafe time can still make the trip feel good.",
      hotelBooking: "Book a hotel in Miyakojima",
      hotelStarTitle: "Hotel dinner and star photography",
      hotelStarTheme: "Enjoy a relaxed hotel dinner, then head to a quiet place for star photography.",
      shootArrivalTitle: "Capture photos and video on a hidden scenic tour",
      shootArrivalTheme: "Spend the afternoon capturing photos and video on a hidden scenic photo tour.",
      shootArrivalWithStarTheme: "Enjoy only the hidden scenic photo tour in the afternoon and star photography at night.",
      yabijiStarTitle: "Yabiji reefs and starry night",
      yabijiStarTheme: "Visit the Yabiji reefs in the morning, stop at a photogenic cafe, then enjoy dinner and star photography.",
      rainyArrivalTitle: "Arrival and rainy-day plan",
      rainyMainTitle: "Enjoy Miyakojima even in the rain",
      rainyMainTheme: "A rainy-day plan with hotel time, slow cafe time, and indoor activities.",
      rainyCafeHotelTitle: "Rainy-day cafe and hotel time",
      rainyCafeHotelTheme: "Keep travel light and spend the day between cafes and your hotel.",
      rainyIndoorPlanTitle: "Make travel memories indoors",
      rainyIndoorPlanTheme: "Even if you cannot go into the ocean, you can enjoy Miyakojima-style activities.",
    },
    nightsOptions: {
      1: "1 night / 2 days",
      2: "2 nights / 3 days",
      3: "3 nights / 4 days",
      4: "4 nights / 5 days",
      5: "5 nights / 6 days",
    },
  },
  中文: {
    kicker: "Miyako Trip Planner",
    title: "制作可以直接用 Google Map 前往的宫古岛行程。",
    lead: "选择住宿天数和想体验的内容，就能生成不赶行程的每日路线。每个地点都可以直接打开 Google Map。",
    nights: "预计住几晚？",
    inputModeTitle: "选择方式",
    inputModes: {
      interests: "想做什么",
      mood: "心情",
    },
    interests: "想做什么？",
    moods: "旅行心情",
    generate: "生成行程",
    route: "用 Google Map 打开当天路线",
    map: "地图",
    routeShort: "路线",
    allRoute: "全部路线",
    allRouteText: "用 Google Map 查看全程",
    result: "推荐行程",
    generatedDone: "已生成行程",
    combinedTheme: "按上午、下午和夜晚分开安排，并预留移动与休息时间。",
    arrivalTitle: "抵达宫古岛",
    arrivalPhotoTitle: "抵达宫古岛・绝景拍摄行程",
    arrivalCafeTitle: "抵达宫古岛・咖啡巡游",
    bookingDefault: "查看预约方案",
    detailDefault: "查看行程详情",
    officialSite: "官方网站",
    storeInfo: "店铺信息",
    videoLabel: "视频",
    mapPreviewTitle: "宫古岛 Google Map",
    departureTheme: "退房后享用早餐，再预留充足时间前往机场。",
    lunchGenres: { spiceCurry: "香料咖喱", curry: "咖喱", localDiner: "当地食堂", cafeMeal: "咖啡店餐食", miyakoBeefCurry: "宫古牛咖喱", ramen: "拉面・沾面" },
    helper: "根据你的选择，把拍摄、海上体验、咖啡、晚餐安排成不太赶的路线。",
    day: "Day",
    saveHint: "目前是行程范例页面。之后可以追加保存和发送预约咨询功能。",
    labels: {
      arrival: "抵达",
      departure: "出发",
      tour: "行程",
      cafe: "咖啡",
      lunch: "午餐",
      dinner: "晚餐",
      beach: "海滩",
      night: "夜生活",
      star: "星空",
      rest: "放松",
      rentacar: "租车",
    },
    interestLabels: {
      photo: "绝景拍摄",
      sup: "秘境SUP",
      cafe: "拍照咖啡店",
      dinner: "当地晚餐",
      night: "夜生活",
      star: "星空拍摄",
      yuni: "Yuni no Hama",
      yaebiji: "八重干濑",
      drone: "无人机拍摄",
      drive: "自驾",
      rentacar: "租车",
      secretBeach: "秘境海滩",
    },
    moodLabels: {
      healing: "想尽情享受疗愈时光",
      packed: "想充分玩遍宫古岛",
      shootSlow: "想好好留下照片和视频",
      selfDrive: "想租车自由巡游",
      rainy: "下雨天做什么？☔️",
    },
    customPlans: {
      hotelDinnerTitle: "在酒店悠闲享用晚餐",
      hotelDinnerDetail: "晚上不要安排太满，在酒店或度假村内悠闲度过。",
      hotelDinnerNote: "热门酒店建议提前预约。",
      hotelRelaxTitle: "在酒店悠闲度过",
      hotelRelaxDetail: "雨天不必勉强外出，也可以在酒店或度假村里放松。",
      rainyIndoorTitle: "雨天室内体验",
      rainyIndoorDetail: "可以体验树脂艺术、狮子彩绘等，把雨天也变成宫古岛旅行回忆。",
      rainyIndoorNote: "有些店可当天预约，但建议先通过 Instagram 或电话确认。",
      rainyIndoorDetailLabel: "查看雨天玩法",
      photogenicCafeTitle: "拍照咖啡店",
      photogenicCafeDetail: "海上活动后，从列表中选择想去的咖啡店悠闲休息。",
      photogenicCafeNote: "可根据当天位置和心情选择。",
      rainyCafeTitle: "在拍照咖啡店悠闲休息",
      rainyCafeDetail: "雨天选择适合室内停留的咖啡店，慢慢度过。",
      rainyCafeNote: "即使不能下海，也能用咖啡时间享受旅行。",
      hotelBooking: "预订宫古岛酒店",
      hotelStarTitle: "酒店晚餐与星空拍摄",
      hotelStarTheme: "在酒店悠闲享用晚餐后，前往安静地点拍摄星空。",
      shootArrivalTitle: "用秘境摄影行程留下照片和视频",
      shootArrivalTheme: "下午参加秘境摄影行程，好好留下照片和视频。",
      shootArrivalWithStarTheme: "下午只安排秘境摄影行程，夜晚悠闲进行星空拍摄。",
      yabijiStarTitle: "八重干濑与星空的一天",
      yabijiStarTheme: "上午前往八重干濑，傍晚去拍照咖啡店，晚餐后享受星空拍摄。",
      rainyArrivalTitle: "抵达宫古岛・雨天计划",
      rainyMainTitle: "雨天也能享受宫古岛",
      rainyMainTheme: "在酒店放松、咖啡店悠闲休息、室内体验留下回忆的雨天计划。",
      rainyCafeHotelTitle: "雨天咖啡与酒店时间",
      rainyCafeHotelTheme: "减少移动，在咖啡店和酒店里悠闲度过。",
      rainyIndoorPlanTitle: "用室内体验留下旅行回忆",
      rainyIndoorPlanTheme: "即使不能下海，也能享受具有宫古岛特色的体验。",
    },
    nightsOptions: {
      1: "1晚2天",
      2: "2晚3天",
      3: "3晚4天",
      4: "4晚5天",
      5: "5晚6天",
    },
  },
  한국어: {
    kicker: "Miyako Trip Planner",
    title: "구글맵으로 바로 이동할 수 있는 미야코지마 일정.",
    lead: "숙박일수와 하고 싶은 것을 고르면 무리 없는 일별 코스를 만들 수 있습니다. 각 장소는 Google Map으로 바로 열 수 있어요.",
    nights: "몇 박 예정인가요?",
    inputModeTitle: "선택 방식",
    inputModes: {
      interests: "하고 싶은 것",
      mood: "기분",
    },
    interests: "하고 싶은 것",
    moods: "여행 분위기",
    generate: "일정 만들기",
    route: "오늘의 루트를 Google Map에서 열기",
    map: "지도",
    routeShort: "경로",
    allRoute: "전체 루트",
    allRouteText: "전체 여행을 Google Map에서 확인",
    result: "추천 일정",
    generatedDone: "일정을 만들었습니다",
    combinedTheme: "오전・오후・저녁으로 나누어 이동과 휴식에 여유를 둔 일정입니다.",
    arrivalTitle: "미야코지마 도착",
    arrivalPhotoTitle: "도착・절경 촬영 투어",
    arrivalCafeTitle: "도착・카페 투어",
    bookingDefault: "예약 플랜 보기",
    detailDefault: "투어 상세 보기",
    officialSite: "공식 사이트",
    storeInfo: "매장 정보",
    videoLabel: "영상",
    mapPreviewTitle: "미야코지마 Google Map",
    departureTheme: "체크아웃 후 아침 식사를 챙기고 여유 있게 공항으로 이동합니다.",
    lunchGenres: { spiceCurry: "스파이스 카레", curry: "카레", localDiner: "로컬 식당", cafeMeal: "카페 식사", miyakoBeefCurry: "미야코규 카레", ramen: "라멘・츠케멘" },
    helper:
      "사진 투어, 바다 체험, 카페, 저녁 식사를 너무 빡빡하지 않게 배치합니다.",
    day: "Day",
    saveHint:
      "현재는 모델 일정 페이지입니다. 이후 저장과 예약 상담 전송 기능도 추가할 수 있습니다.",
    labels: {
      arrival: "도착",
      departure: "출발",
      tour: "투어",
      cafe: "카페",
      lunch: "점심",
      dinner: "저녁",
      beach: "비치",
      night: "밤",
      star: "별",
      rest: "여유",
      rentacar: "렌터카",
    },
    interestLabels: {
      photo: "절경 촬영",
      sup: "숨은 SUP",
      cafe: "사진 카페",
      dinner: "로컬 저녁",
      night: "밤 놀이",
      star: "별 촬영",
      yuni: "유니노하마",
      yaebiji: "야에비지",
      drone: "드론 촬영",
      drive: "드라이브",
      rentacar: "렌터카",
      secretBeach: "시크릿 비치",
    },
    moodLabels: {
      healing: "힐링 시간을 만끽하고 싶어요",
      packed: "미야코지마를 알차게 즐기고 싶어요",
      shootSlow: "사진과 영상을 제대로 남기고 싶어요",
      selfDrive: "렌터카로 자유롭게 둘러보고 싶어요",
      rainy: "비 오는 날 뭐 하지? ☔️",
    },
    customPlans: {
      hotelDinnerTitle: "호텔에서 여유롭게 저녁 식사",
      hotelDinnerDetail: "저녁은 일정을 무리하게 넣지 않고 호텔이나 리조트 안에서 여유롭게 보냅니다.",
      hotelDinnerNote: "인기 호텔은 사전 예약을 추천합니다.",
      hotelRelaxTitle: "호텔에서 여유롭게 보내기",
      hotelRelaxDetail: "비 오는 날에는 무리해서 이동하지 않고 호텔이나 리조트에서 쉬는 것도 좋습니다.",
      rainyIndoorTitle: "비 오는 날 실내 체험",
      rainyIndoorDetail: "레진 아트나 시사 만들기 등으로 비 오는 날도 미야코지마다운 추억을 남길 수 있습니다.",
      rainyIndoorNote: "당일 예약이 가능한 경우도 있지만 Instagram이나 전화로 미리 확인하는 것을 추천합니다.",
      rainyIndoorDetailLabel: "비 오는 날 코스 보기",
      photogenicCafeTitle: "사진 예쁜 카페",
      photogenicCafeDetail: "바다 체험 후에는 목록에서 가고 싶은 카페를 골라 여유롭게 쉽니다.",
      photogenicCafeNote: "이동 위치와 그날의 기분에 맞춰 고를 수 있습니다.",
      rainyCafeTitle: "사진 예쁜 카페에서 여유롭게",
      rainyCafeDetail: "비 오는 날에는 실내에서 머물기 좋은 카페를 골라 천천히 쉽니다.",
      rainyCafeNote: "바다에 들어가지 못해도 카페 시간을 잘 활용하면 여행을 즐길 수 있습니다.",
      hotelBooking: "미야코지마 호텔 예약하기",
      hotelStarTitle: "호텔 저녁과 별 촬영",
      hotelStarTheme: "호텔에서 여유롭게 저녁을 즐긴 뒤 조용한 장소에서 별 촬영을 합니다.",
      shootArrivalTitle: "비경 포토 투어로 사진과 영상 남기기",
      shootArrivalTheme: "오후에는 비경 포토 투어로 사진과 영상을 제대로 남깁니다.",
      shootArrivalWithStarTheme: "오후에는 비경 포토 투어, 밤에는 별 촬영만 여유롭게 즐깁니다.",
      yabijiStarTitle: "야에비지와 별빛을 즐기는 날",
      yabijiStarTheme: "오전에는 야에비지 투어, 오후에는 사진 예쁜 카페, 저녁 식사 후 별 촬영을 즐깁니다.",
      rainyArrivalTitle: "미야코지마 도착・비 오는 날 플랜",
      rainyMainTitle: "비 오는 날에도 즐기는 미야코지마",
      rainyMainTheme: "호텔에서 쉬고, 카페에서 여유롭게 보내고, 실내 체험으로 추억을 남기는 비 오는 날 플랜입니다.",
      rainyCafeHotelTitle: "비 오는 날 카페와 호텔 시간",
      rainyCafeHotelTheme: "이동을 줄이고 카페와 호텔에서 여유롭게 보냅니다.",
      rainyIndoorPlanTitle: "실내 체험으로 여행 추억 만들기",
      rainyIndoorPlanTheme: "바다에 들어가지 못하는 날에도 미야코지마다운 체험을 즐길 수 있습니다.",
    },
    nightsOptions: {
      1: "1박 2일",
      2: "2박 3일",
      3: "3박 4일",
      4: "4박 5일",
      5: "5박 6일",
    },
  },
};

const localizedPlans = {
  JP: {
    arrival: [
      {
        time: "09:00",
        category: "arrival",
        title: "みやこ下地島空港",
        detail: "到着後、レンタカー受け取りまで空港で軽く休憩。",
        note: "飲み物や日焼け止めをここで準備しておくと安心です。",
        mapQuery: "みやこ下地島空港",
      },
      {
        time: "10:00",
        category: "rentacar",
        title: "51レンタカー下地店",
        detail: "空港到着後、レンタカーを借りて宮古島の旅をスタート。",
        note: "〒906-0504 沖縄県宮古島市伊良部仲地96−20 / 電話 0980-79-9951",
        mapQuery: "51レンタカー下地店 沖縄県宮古島市伊良部仲地96-20",
        websiteUrl: "https://51inc.jp/",
        phoneUrl: "tel:0980799951",
      },
      {
        time: "11:30",
        category: "cafe",
        title: "Blue Turtle",
        detail: "伊良部島の海、渡口の浜を見ながらゆっくりできます。",
        note: "海が見えるカフェで休憩。",
        mapQuery: "Blue Turtle 宮古島",
      },
      {
        time: "14:00",
        category: "beach",
        title: "17END",
        detail: "宮古ブルーが見られる絶景スポット。",
        note: "干潮の時間に行くのがおすすめ。",
        mapQuery: "17END 宮古島",
      },
    ],
    departure: [
      {
        time: "09:00",
        category: "departure",
        title: "ホテルチェックアウト",
        detail: "忘れ物を確認してホテルをチェックアウト。",
        note: "荷物を車に積んで、朝ごはんへ向かいます。",
        hideMedia: true,
      },
      {
        time: "09:30",
        category: "cafe",
        title: "朝ごはん",
        detail: "くじら食堂で朝ごはんをテイクアウト。",
        mapQuery: "くじら食堂 宮古島",
      },
      {
        time: "11:30",
        category: "departure",
        title: "みやこ下地島空港へ",
        detail: "レンタカーを返却して空港へ向かいます。",
        note: "フライト時刻に合わせて出発時間を調整してください。",
        mapQuery: "みやこ下地島空港",
        hideMedia: true,
      },
    ],
    photo: {
      title: "秘境フォトツアーで撮影",
      theme: "秘境スポットや撮影スポットを巡り、旅の写真と映像を残す日。",
      items: [
        {
          time: "09:30",
          category: "tour",
          tourType: "photo",
          title: "絶景撮影ツアー",
          detail: "宮古島の絶景や秘境、シークレットビーチを巡りながら撮影します。",
          note: "ドローン撮影・一眼カメラ撮影付き。",
          mapQuery: "宮古島 絶景スポット",
          detailUrl: "/tours/one-day-photogenic-tour",
          detailLabel: "秘境撮影ツアーの詳細",
        },
        {
          time: "13:30",
          category: "cafe",
          title: "OHAMAテラス",
          detail: "池間島の海を眺めながら、撮影後のランチとカフェ休憩。",
          note: "午後の明るい時間は海とテラスをきれいに撮影できます。",
          mapQuery: "OHAMAテラス 宮古島",
          instagramUrl: "https://www.instagram.com/ohamaterasu/",
        },
        {
          time: "15:30",
          category: "cafe",
          title: "HARRY'S Shrimp Truck",
          detail: "北部エリアを移動しながら、写真映えするシュリンプトラックへ。",
          note: "移動を詰め込みすぎず、夕方前にはゆっくり休憩します。",
          mapQuery: "HARRY'S Shrimp Truck 宮古島",
          instagramUrl: "https://www.instagram.com/harrysshrimptruck/",
        },
      ],
    },
    sup: {
      title: "秘境SUPで海を独り占め",
      theme: "涙が出るほど綺麗な海で、SUPと撮影を楽しむ日。",
      items: [
        {
          time: "13:00",
          category: "tour",
          tourType: "sup",
          title: "秘境SUPツアー",
          detail: "誰もいないビーチで、あなただけの時間を満喫。",
          note: "癒しを求める方におすすめの究極の非日常体験です。",
          mapQuery: "宮古島 SUP",
          detailUrl: "/tours/hidden-sup",
          detailLabel: "秘境SUPの詳細",
        },
        {
          time: "12:30",
          category: "lunch",
          title: "島ごはんランチ",
          detail: "ツアー後の気分に合わせて、宮古島のランチを選べます。",
          note: "濡れた後に着替えやすい服装がおすすめです。",
          mapQuery: "宮古島 ランチ",
        },
      ],
    },
    jet: {
      title: "JETでユニの浜・秘境ビーチへ",
      theme: "ジェットでしか行けないシークレットビーチを巡る日。",
      items: [
        {
          time: "10:00",
          category: "tour",
          tourType: "jet",
          title: "JET秘境ビーチツアー",
          detail: "ユニの浜と秘境・シークレットビーチへご案内。",
          note: "完全プライベート、ドローン撮影付き。",
          mapQuery: "ユニの浜 宮古島",
        },
        {
          time: "13:30",
          category: "beach",
          title: "人が少ないビーチで休憩",
          detail: "午後は予定を詰め込まず、海の余韻を楽しむ時間に。",
          note: "疲れすぎないよう、夕方はゆっくりがおすすめです。",
          mapQuery: "宮古島 人が少ない ビーチ",
        },
      ],
    },
    yuni: {
      title: "ユニの浜へ行く日",
      theme: "潮の時間に合わせて、海に浮かぶ幻の砂浜を楽しむ日。",
      items: [
        {
          time: "09:30",
          category: "tour",
          tourType: "yuni",
          title: "ユニの浜ツアー",
          detail: "ボートやマリンアクティビティで、干潮時に現れるユニの浜へ。",
          note: "潮位や海況により出発時間・催行内容が変わるため、事前予約がおすすめです。",
          bookingUrl: "https://activityjapan.com/search/okinawa/okinawa_r/344/?addressAndKeyword=%E3%83%A6%E3%83%8B%E3%81%AE%E6%B5%9C",
        },
        {
          time: "13:00",
          category: "cafe",
          title: "島カフェでランチ",
          detail: "ツアー後は近いエリアのカフェで、無理なくランチと休憩。",
          note: "濡れても過ごしやすい服装と着替えを用意しておくと安心です。",
          mapQuery: "宮古島 カフェ ランチ",
        },
      ],
    },
    yaebiji: {
      title: "八重干瀬の海を楽しむ日",
      theme: "ボートで八重干瀬へ向かい、シュノーケルと撮影を楽しむ日。",
      items: [
        {
          time: "09:00",
          category: "tour",
          tourType: "yaebiji",
          title: "八重干瀬ツアー",
          detail: "日本最大級のサンゴ礁群・八重干瀬で、シュノーケルと水中撮影。",
          note: "所要時間は約4〜5時間。海況によりポイントや催行内容が変わります。",
          bookingUrl: "https://activityjapan.com/search/okinawa/okinawa_r/344/?addressAndKeyword=%E3%83%A4%E3%83%93%E3%82%B8",
          bookingLabel: "八重干瀬ツアーを予約",
        },
        {
          time: "14:30",
          category: "cafe",
          title: "映えるカフェ",
          detail: "海遊びの後は、行きたいカフェを一覧から選んでゆっくり休憩。",
          note: "港からの移動やその日の気分に合わせて選べます。",
          mapQuery: "宮古島 映えるカフェ",
        },
      ],
    },
    cafe: {
      title: "カフェ巡りと島時間",
      theme: "映えるカフェを巡りながら、ゆっくり過ごす日。",
      items: [
        {
          time: "10:30",
          category: "cafe",
          title: "Pani Pani",
          detail: "来間島の緑に囲まれたカフェで、南国らしい写真と朝のカフェ時間。",
          mapQuery: "Pani Pani 来間島",
        },
        {
          time: "12:30",
          category: "cafe",
          title: "島茶家ヤッカヤッカ",
          detail: "来間島で、コク深い宮古牛のコク旨カレーを楽しみます。",
          note: "Pani Paniから近く、無理なく続けて立ち寄れます。",
          mapQuery: "島茶家ヤッカヤッカ 宮古島",
          instagramUrl: "https://www.instagram.com/reel/ClqeHlmjGq_/",
        },
        {
          time: "18:30",
          category: "cafe",
          title: "SHISA'S CAFE & BBQ",
          detail: "海を眺めながら、夕暮れのサンセットを楽しみます。",
          note: "サンセットに合わせて18:30頃の到着がおすすめです。",
          mapQuery: "SHISA'S CAFE & BBQ 宮古島",
          instagramUrl: "https://www.instagram.com/shisas_cafe_bbq/",
        },
      ],
    },
    dinner: {
      title: "宮古島の夜ご飯を楽しむ",
      theme: "島料理・宮古牛・しゃぶしゃぶから、その日の気分で選べる夜ご飯。",
      items: [
        {
          time: "18:30",
          category: "dinner",
          title: "おすすめの夜ご飯屋",
          detail: "気になるお店を選んで、Instagramで料理や店内の雰囲気を確認できます。",
          note: "人気店なので早めの予約がおすすめです。",
          mapQuery: "宮古島 夜ご飯 人気",
        },
        {
          time: "20:30",
          category: "night",
          title: "夜遊びスポット / 夜カフェ",
          detail:
            "朝まで営業してるバー / 果実屋。バーや夜カフェなど宮古島の夜を満喫。",
          note: "翌日の予定に響かない範囲で楽しみましょう。",
          mapQuery: "宮古島 朝まで営業 バー",
        },
      ],
    },
    night: {
      title: "宮古島の夜遊び",
      theme: "バーや夜カフェなど、宮古島の夜を満喫する日。",
      items: [
        {
          time: "20:00",
          category: "night",
          title: "朝まで営業してるバー",
          detail: "旅行の夜に少し遊びたい方におすすめの夜スポット。",
          note: "翌日の予定に響かない範囲で楽しみましょう。",
          mapQuery: "宮古島 朝まで営業 バー",
        },
        {
          time: "21:30",
          category: "night",
          title: "果実屋",
          detail: "夜カフェ感覚で立ち寄れる宮古島の夜スポット。",
          note: "夜ご飯後に軽く寄る流れがおすすめです。",
          mapQuery: "果実屋 宮古島",
        },
      ],
    },
    star: {
      title: "星空撮影と静かな夜",
      theme: "月明かりや雲を見ながら、星空が見えやすい場所へ。",
      items: [
        {
          time: "20:30",
          category: "star",
          title: "星空撮影",
          detail: "料金は1人7,000円。街灯のない真っ暗な場所で撮影します。",
          note: "現地集合。送迎ありの場合は＋2,000円／グループ。",
          bookingUrl: "https://lin.ee/yFgNdA8",
          bookingLabel: "LINEで予約",
        },
      ],
    },
    beach: {
      title: "人が少ないビーチでのんびり",
      theme: "観光客が行かない、人が少ないビーチをこっそり巡る日。",
      items: [
        {
          time: "10:00",
          category: "beach",
          title: "静かなビーチへ",
          detail: "観光客が行かない人が少ないビーチをこっそり教えます。",
          note: "潮位と風向きで行き先を変えると、より綺麗な海に出会えます。",
          mapQuery: "宮古島 穴場 ビーチ",
        },
      ],
    },
    secretBeach: {
      title: "シークレットビーチ巡り",
      theme: "観光客が行かない、人が少ない秘境ビーチを巡る日。",
      items: [
        {
          time: "10:00",
          category: "beach",
          title: "シークレットビーチ",
          detail: "人が少ないビーチを、その日の潮位や風向きに合わせてご案内。",
          note: "場所は安全面を考えて当日のコンディションで決めるのがおすすめです。",
          mapQuery: "宮古島 シークレットビーチ",
          detailUrl: "/tours/hidden-sup",
          detailLabel: "秘境SUPの詳細",
        },
      ],
    },
    rentacar: {
      title: "レンタカーで島ドライブ",
      theme: "橋や海沿いを巡りながら、自由に宮古島を楽しむ日。",
      items: [
        {
          time: "09:30",
          category: "rest",
          categoryLabel: "ドライブ",
          title: "レンタカーで絶景ドライブ",
          detail: "伊良部大橋、池間大橋、来間大橋などを巡りながら写真撮影。",
          note: "移動距離が長くなりすぎないよう、エリアごとにまとめるのがおすすめです。",
          mapQuery: "宮古島 ドライブ 絶景",
        },
      ],
    },
    rest: {
      title: "池間島ドライブ",
      theme: "池間大橋を渡り、ビーチ・カフェ・島グルメをゆっくり巡る日。",
      items: [
        {
          time: "09:30",
          category: "rest",
          categoryLabel: "ドライブ",
          title: "池間大橋で撮影",
          detail: "池間ブルーを眺めながら、池間大橋周辺で写真を撮ります。",
          note: "橋の途中では停車せず、安全な駐車スペースから撮影します。",
          mapQuery: "池間大橋",
          instagramUrl: "https://www.instagram.com/reel/DWgKV-3kQCS/",
        },
        {
          time: "10:30",
          category: "beach",
          title: "ハート岩＆イキヅービーチ",
          detail: "ハート型に見える岩と、池間島の静かなビーチを楽しみます。",
          note: "潮位によって見え方が変わります。",
          mapQuery: "ハート岩 イキヅービーチ 池間島",
          instagramUrl: "https://www.instagram.com/reel/DbfuIP2tDsu/",
        },
        {
          time: "11:30",
          category: "beach",
          title: "フナクスビーチ",
          detail: "透明度の高い海を眺めながら、ビーチをゆっくり散策します。",
          note: "泳ぐ場合は海況を確認し、安全に楽しみましょう。",
          mapQuery: "フナクスビーチ 池間島",
          instagramUrl: "https://www.instagram.com/reel/C6pNMSoSgSf/",
        },
        {
          time: "12:30",
          category: "cafe",
          categoryLabel: "スイーツ",
          title: "海美来",
          detail: "池間島名物の紅芋もちを楽しみます。",
          note: "売り切れる場合があるため、早めの時間がおすすめです。",
          mapQuery: "海美来 池間島",
          instagramUrl: "https://www.instagram.com/p/Ck_Dl_kytYp/",
        },
        {
          time: "14:00",
          category: "cafe",
          title: "Gelato cafe Ninufa",
          detail: "池間島の海を眺めながら、島素材のジェラートで休憩。",
          note: "全席オーシャンビューのテラスカフェです。",
          mapQuery: "Gelato cafe Ninufa 池間島",
          instagramUrl: "https://www.instagram.com/reel/DYEeG9rz_Ks/",
        },
        {
          time: "15:30",
          category: "cafe",
          title: "OHAMAテラス",
          detail: "池間島の海を見渡せるテラスで、ドライブの最後にゆっくり休憩。",
          note: "閉店時間や営業日は事前にInstagramで確認してください。",
          mapQuery: "OHAMAテラス 池間島",
          instagramUrl: "https://www.instagram.com/p/DGRlEe7z8d5/",
        },
      ],
    },
  },
};

localizedPlans.EN = {
  arrival: [
    {
      time: "13:00",
      category: "arrival",
      title: "Miyako Shimojishima Airport",
      detail:
        "Arrive and take a short break before picking up your rental car.",
      note: "Prepare drinks and sunscreen before heading out.",
      mapQuery: "Miyako Shimojishima Airport",
    },
    {
      time: "15:20",
      category: "cafe",
      title: "Blue Turtle",
      detail: "Start the trip slowly with an ocean-view cafe on Irabu Island.",
      note: "A relaxed first-day stop that is easy to photograph.",
      mapQuery: "Blue Turtle Miyakojima",
    },
    {
      time: "17:00",
      category: "beach",
      title: "17END",
      detail: "See the clear Miyako blue around Shimoji Island.",
      note: "The scenery changes beautifully with the tide.",
      mapQuery: "17END Miyakojima",
    },
  ],
  departure: [
    {
      time: "09:30",
      category: "cafe",
      title: "Slow breakfast at a cafe",
      detail: "Keep the final morning relaxed before heading to the airport.",
      note: "Choose somewhere close depending on your flight time.",
      mapQuery: "Miyakojima cafe breakfast",
    },
    {
      time: "11:30",
      category: "departure",
      title: "Miyako Shimojishima Airport",
      detail: "Return the rental car and head to the airport.",
      note: "Leave a little time for souvenirs.",
      mapQuery: "Miyako Shimojishima Airport",
    },
  ],
  photo: {
    title: "Scenic Photo Tour",
    theme: "Spend the day visiting hidden scenic spots and capturing the trip.",
    items: [
      {
        time: "09:30",
        category: "tour",
        tourType: "photo",
        title: "Private Scenic Photo Tour",
        detail:
          "Visit beautiful and quiet places around Miyakojima with photo support.",
        note: "Includes drone and camera shooting.",
        mapQuery: "Miyakojima scenic spots",
      },
      {
        time: "13:00",
        category: "cafe",
        title: "Photogenic Cafe",
        detail: "Seahorse club cafe, OHAMA Terrace, Pani Pani, and more.",
        note: "Daylight makes the photos cleaner and brighter.",
        mapQuery: "Miyakojima photogenic cafe",
      },
    ],
  },
  sup: {
    title: "Hidden SUP Experience",
    theme: "Enjoy SUP and filming in water so clear it feels unreal.",
    items: [
      {
        time: "13:00",
        category: "tour",
        tourType: "sup",
        title: "Hidden SUP Tour",
        detail: "Head to a quiet point depending on sea conditions.",
        note: "Photos, videos, and drone shooting can be included.",
        mapQuery: "Miyakojima SUP",
      },
      {
        time: "12:30",
        category: "dinner",
        title: "Local Lunch",
        detail: "Have an easy lunch in town after the tour.",
        note: "Bring clothes that are easy to change into after the ocean.",
        mapQuery: "Miyakojima lunch",
      },
    ],
  },
  jet: {
    title: "Private Jet Tour to Secret Beaches",
    theme: "Visit Yuni no Hama and secret beaches only reachable by jet.",
    items: [
      {
        time: "10:00",
        category: "tour",
        tourType: "jet",
        title: "Jet Secret Beach Tour",
        detail: "Explore Yuni no Hama and quiet secret beaches privately.",
        note: "Private tour with drone shooting.",
        mapQuery: "Yuni no Hama Miyakojima",
      },
      {
        time: "13:30",
        category: "beach",
        title: "Quiet Beach Break",
        detail: "Keep the afternoon open and enjoy the afterglow of the sea.",
        note: "A slower evening keeps the trip comfortable.",
        mapQuery: "Miyakojima quiet beach",
      },
    ],
  },
  cafe: {
    title: "Cafe Hopping and Island Time",
    theme: "Visit photogenic cafes and spend the day slowly.",
    items: [
      {
        time: "10:30",
        category: "cafe",
        title: "Seahorse club cafe",
        detail: "A cafe with a mood you will want to keep in photos.",
        note: "Late morning to early afternoon is easiest for photos.",
        mapQuery: "Seahorse club cafe Miyakojima",
      },
      {
        time: "14:00",
        category: "cafe",
        title: "OHAMA Terrace / Pani Pani",
        detail: "Add another cafe with ocean or tropical scenery.",
        note: "Group cafes by area to reduce driving time.",
        mapQuery: "OHAMA Terrace Miyakojima",
      },
    ],
  },
  dinner: {
    title: "Local Dinner and Nightlife",
    theme: "Enjoy Miyakojima food and a little island nightlife.",
    items: [
      {
        time: "18:30",
        category: "dinner",
        title: "Recommended Local Dinner",
        detail:
          "Takara / Churashima / Deiriguchi / Umarizuma / Yakiniku Kihachi.",
        note: "Popular restaurants should be reserved early.",
        mapQuery: "Miyakojima dinner popular",
      },
      {
        time: "20:30",
        category: "night",
        title: "Muscle Bar / Kajitsuya",
        detail: "Enjoy a bar or night cafe in Miyakojima.",
        note: "Keep it light if you have plans the next morning.",
        mapQuery: "Muscle Bar Miyakojima",
      },
    ],
  },
  night: {
    title: "Miyakojima Nightlife",
    theme: "Enjoy bars and night cafes in Miyakojima.",
    items: [
      {
        time: "20:00",
        category: "night",
        title: "Muscle Bar",
        detail: "A fun night spot for travelers who want a little nightlife.",
        note: "Keep it light if you have plans the next morning.",
        mapQuery: "Muscle Bar Miyakojima",
      },
      {
        time: "21:30",
        category: "night",
        title: "Kajitsuya",
        detail: "A relaxed night-cafe style stop after dinner.",
        note: "Good as a short stop after local dinner.",
        mapQuery: "Kajitsuya Miyakojima",
      },
    ],
  },
  star: {
    title: "Starry Sky Photo Night",
    theme: "Move to a darker place depending on clouds and moonlight.",
    items: [
      {
        time: "20:30",
        category: "star",
        title: "Star Photo Session",
        detail: "Shoot near the coast or open areas with fewer streetlights.",
        note: "Book this early in the trip so the date can be adjusted for weather.",
        mapQuery: "Miyakojima starry sky spot",
      },
    ],
  },
  beach: {
    title: "Quiet Beach Day",
    theme: "Visit peaceful beaches with fewer tourists.",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "Quiet Local Beach",
        detail: "We can suggest beaches that most tourists do not visit.",
        note: "The best beach changes with tide and wind.",
        mapQuery: "Miyakojima hidden beach",
      },
    ],
  },
  secretBeach: {
    title: "Secret Beach Hopping",
    theme: "Visit quiet hidden beaches away from the usual tourist flow.",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "Secret Beach",
        detail: "Choose a quiet beach depending on the tide and wind.",
        note: "The exact location is best decided based on safety and conditions.",
        mapQuery: "Miyakojima secret beach",
      },
    ],
  },
  rentacar: {
    title: "Rental Car Island Drive",
    theme: "Drive around bridges and coastal roads at your own pace.",
    items: [
      {
        time: "09:30",
        category: "rest",
        title: "Scenic Rental Car Drive",
        detail:
          "Visit Irabu Bridge, Ikema Bridge, Kurima Bridge, and coastal viewpoints.",
        note: "Group spots by area so the drive does not become too long.",
        mapQuery: "Miyakojima scenic drive",
      },
    ],
  },
  rest: {
    title: "Island Drive",
    theme: "Enjoy a flexible day with scenic drives and open time.",
    items: [
      {
        time: "10:00",
        category: "rest",
        title: "Miyakojima Scenic Drive",
        detail:
          "Cross bridges, follow the coast, and stop for photos when you feel like it.",
        note: "One open day makes the whole trip feel easier.",
        mapQuery: "Miyakojima scenic drive",
      },
    ],
  },
};

localizedPlans.中文 = {
  arrival: [
    {
      time: "13:00",
      category: "arrival",
      title: "宫古下地岛机场",
      detail: "抵达后，在取车前先于机场稍作休息。",
      note: "建议先准备饮料、防晒等用品。",
      mapQuery: "宫古下地岛机场",
    },
    {
      time: "15:20",
      category: "cafe",
      title: "Blue Turtle",
      detail: "在伊良部岛海景咖啡店慢慢开始旅程。",
      note: "很适合留下抵达当天的照片。",
      mapQuery: "Blue Turtle 宫古岛",
    },
    {
      time: "17:00",
      category: "beach",
      title: "17END",
      detail: "前往下地岛代表性的宫古蓝绝景。",
      note: "景色会随潮位变化。",
      mapQuery: "17END 宫古岛",
    },
  ],
  departure: [
    {
      time: "09:30",
      category: "cafe",
      title: "咖啡店早餐",
      detail: "最后一天不要排太满，感受岛上的慢节奏后前往机场。",
      note: "可根据航班时间选择附近店铺。",
      mapQuery: "宫古岛 早餐 咖啡",
    },
    {
      time: "11:30",
      category: "departure",
      title: "宫古下地岛机场",
      detail: "还车后前往机场。",
      note: "建议预留一点购买伴手礼的时间。",
      mapQuery: "宫古下地岛机场",
    },
  ],
  photo: {
    title: "绝景拍摄行程",
    theme: "巡游秘境和拍摄点，把旅程用照片和影像留下。",
    items: [
      {
        time: "09:30",
        category: "tour",
        tourType: "photo",
        title: "私人绝景拍摄",
        detail: "一边游览宫古岛绝景与秘境，一边进行拍摄。",
        note: "包含无人机与相机拍摄。",
        mapQuery: "宫古岛 绝景",
      },
      {
        time: "13:00",
        category: "cafe",
        title: "适合拍照的咖啡店",
        detail: "Seahorse club cafe / OHAMA Terrace / Pani Pani 等。",
        note: "白天光线好，照片更漂亮。",
        mapQuery: "宫古岛 网红咖啡",
      },
    ],
  },
  sup: {
    title: "秘境SUP体验",
    theme: "在透明度极高的海上体验SUP与拍摄。",
    items: [
      {
        time: "13:00",
        category: "tour",
        tourType: "sup",
        title: "秘境SUP行程",
        detail: "根据海况前往游客较少的地点。",
        note: "可体验照片、视频与无人机拍摄。",
        mapQuery: "宫古岛 SUP",
      },
      {
        time: "12:30",
        category: "dinner",
        title: "岛上午餐",
        detail: "体验后在市区轻松吃午餐。",
        note: "建议准备方便更换的衣服。",
        mapQuery: "宫古岛 午餐",
      },
    ],
  },
  jet: {
    title: "JET秘境海滩行程",
    theme: "前往只有JET才能到达的Yuni海滩与秘境海滩。",
    items: [
      {
        time: "10:00",
        category: "tour",
        tourType: "jet",
        title: "JET秘境海滩",
        detail: "私人带你前往Yuni no Hama和静谧秘境海滩。",
        note: "私人行程，含无人机拍摄。",
        mapQuery: "Yuni no Hama 宫古岛",
      },
      {
        time: "13:30",
        category: "beach",
        title: "安静海滩休息",
        detail: "下午不排太满，慢慢享受海边余韵。",
        note: "轻松一点会让旅程更舒服。",
        mapQuery: "宫古岛 安静海滩",
      },
    ],
  },
  cafe: {
    title: "咖啡巡游与岛上慢时光",
    theme: "前往适合拍照的咖啡店，轻松享受一天。",
    items: [
      {
        time: "10:30",
        category: "cafe",
        title: "Seahorse club cafe",
        detail: "氛围很好、很想拍照留念的咖啡店。",
        note: "上午到午后光线比较适合拍摄。",
        mapQuery: "Seahorse club cafe 宫古岛",
      },
      {
        time: "14:00",
        category: "cafe",
        title: "OHAMA Terrace / Pani Pani",
        detail: "再安排一家有海景或南国氛围的咖啡店。",
        note: "按区域安排会减少开车时间。",
        mapQuery: "OHAMA Terrace 宫古岛",
      },
    ],
  },
  dinner: {
    title: "宫古岛晚餐与夜生活",
    theme: "享受宫古岛当地美食和轻松夜晚。",
    items: [
      {
        time: "18:30",
        category: "dinner",
        title: "推荐晚餐",
        detail:
          "岛おでんたから / 美ら島 / でいりぐち / うまりずま / 焼肉喜八。",
        note: "热门店建议提前预约。",
        mapQuery: "宫古岛 晚餐 推荐",
      },
      {
        time: "20:30",
        category: "night",
        title: "Muscle Bar / 果实屋",
        detail: "酒吧或夜咖啡，感受宫古岛夜晚。",
        note: "第二天有行程的话，建议轻松体验即可。",
        mapQuery: "Muscle Bar 宫古岛",
      },
    ],
  },
  night: {
    title: "宫古岛夜生活",
    theme: "酒吧和夜咖啡，轻松体验宫古岛的夜晚。",
    items: [
      {
        time: "20:00",
        category: "night",
        title: "Muscle Bar",
        detail: "想在旅行夜晚稍微热闹一下时很适合。",
        note: "第二天有行程的话建议不要太晚。",
        mapQuery: "Muscle Bar 宫古岛",
      },
      {
        time: "21:30",
        category: "night",
        title: "果实屋",
        detail: "晚餐后可以轻松去的夜咖啡风格地点。",
        note: "适合晚餐后短暂停留。",
        mapQuery: "果实屋 宫古岛",
      },
    ],
  },
  star: {
    title: "星空拍摄之夜",
    theme: "根据云量和月光，前往更暗、更适合看星星的地方。",
    items: [
      {
        time: "20:30",
        category: "star",
        title: "星空拍摄",
        detail: "前往街灯较少的海边或开阔地点拍摄。",
        note: "建议安排在旅行前半，遇到天气不好时更容易调整。",
        mapQuery: "宫古岛 星空 景点",
      },
    ],
  },
  beach: {
    title: "安静海滩放松",
    theme: "去游客较少的海滩，享受更安静的宫古岛。",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "当地安静海滩",
        detail: "介绍游客不太会去、人较少的海滩。",
        note: "根据潮位和风向选择地点会更漂亮。",
        mapQuery: "宫古岛 秘境 海滩",
      },
    ],
  },
  secretBeach: {
    title: "秘境海滩巡游",
    theme: "前往游客较少的秘境海滩。",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "秘境海滩",
        detail: "根据潮位和风向选择人少、安静的海滩。",
        note: "具体地点建议根据当天安全状况决定。",
        mapQuery: "宫古岛 秘境 海滩",
      },
    ],
  },
  rentacar: {
    title: "租车环岛兜风",
    theme: "沿着大桥与海岸线，自由感受宫古岛。",
    items: [
      {
        time: "09:30",
        category: "rest",
        title: "租车绝景兜风",
        detail: "伊良部大桥、池间大桥、来间大桥和海边景点。",
        note: "按区域安排，开车会更轻松。",
        mapQuery: "宫古岛 自驾 绝景",
      },
    ],
  },
  rest: {
    title: "岛上兜风",
    theme: "不把行程排太满，享受宫古岛的自由时间。",
    items: [
      {
        time: "10:00",
        category: "rest",
        title: "宫古岛绝景兜风",
        detail: "经过桥梁和海岸线，看到喜欢的地方就停下来拍照。",
        note: "安排一天留白，会让整趟旅行更舒服。",
        mapQuery: "宫古岛 自驾 绝景",
      },
    ],
  },
};

localizedPlans.한국어 = {
  ...localizedPlans.EN,
  arrival: [
    {
      time: "13:00",
      category: "arrival",
      title: "미야코 시모지시마 공항",
      detail: "도착 후 렌터카 픽업 전 공항에서 잠시 쉬어갑니다.",
      note: "음료와 선크림을 먼저 준비해두면 좋아요.",
      mapQuery: "Miyako Shimojishima Airport",
    },
    {
      time: "15:20",
      category: "cafe",
      title: "Blue Turtle",
      detail: "이라부섬 오션뷰 카페에서 여행을 천천히 시작합니다.",
      note: "도착일 사진을 남기기 좋은 장소입니다.",
      mapQuery: "Blue Turtle Miyakojima",
    },
    {
      time: "17:00",
      category: "beach",
      title: "17END",
      detail: "시모지섬다운 미야코 블루를 만나는 절경 포인트.",
      note: "조수에 따라 풍경이 달라집니다.",
      mapQuery: "17END Miyakojima",
    },
  ],
  departure: [
    {
      time: "09:30",
      category: "cafe",
      title: "카페에서 아침",
      detail: "마지막 날은 여유롭게 섬의 시간을 느끼고 공항으로 향합니다.",
      note: "비행 시간에 맞춰 가까운 카페를 고르세요.",
      mapQuery: "Miyakojima cafe breakfast",
    },
    {
      time: "11:30",
      category: "departure",
      title: "미야코 시모지시마 공항",
      detail: "렌터카를 반납하고 공항으로 이동합니다.",
      note: "기념품을 볼 시간도 조금 남겨두면 좋습니다.",
      mapQuery: "Miyako Shimojishima Airport",
    },
  ],
  photo: {
    title: "절경 촬영 투어",
    theme: "숨은 명소와 촬영 포인트를 돌며 여행의 사진과 영상을 남기는 날.",
    items: [
      {
        time: "09:30",
        category: "tour",
        tourType: "photo",
        title: "프라이빗 절경 촬영",
        detail: "미야코지마의 아름답고 조용한 장소를 돌며 촬영합니다.",
        note: "드론 촬영과 카메라 촬영 포함.",
        mapQuery: "Miyakojima scenic spots",
      },
      {
        time: "13:00",
        category: "cafe",
        title: "사진 찍기 좋은 카페",
        detail: "Seahorse club cafe / OHAMA Terrace / Pani Pani 등.",
        note: "밝은 시간대가 사진이 더 예쁘게 나옵니다.",
        mapQuery: "Miyakojima photogenic cafe",
      },
    ],
  },
  sup: {
    title: "숨은 SUP 체험",
    theme: "눈물이 날 만큼 투명한 바다에서 SUP와 촬영을 즐기는 날.",
    items: [
      {
        time: "13:00",
        category: "tour",
        tourType: "sup",
        title: "숨은 SUP 투어",
        detail: "해상 상황에 맞춰 사람이 적은 포인트로 이동합니다.",
        note: "사진, 영상, 드론 촬영까지 즐길 수 있습니다.",
        mapQuery: "Miyakojima SUP",
      },
      {
        time: "12:30",
        category: "dinner",
        title: "로컬 점심",
        detail: "투어 후 시내에서 부담 없이 점심을 먹습니다.",
        note: "바다 후 갈아입기 쉬운 옷을 추천합니다.",
        mapQuery: "Miyakojima lunch",
      },
    ],
  },
  jet: {
    title: "JET로 가는 유니의 하마와 시크릿 비치",
    theme: "JET로만 갈 수 있는 비밀 해변을 둘러보는 날.",
    items: [
      {
        time: "10:00",
        category: "tour",
        tourType: "jet",
        title: "JET 시크릿 비치 투어",
        detail: "유니의 하마와 조용한 시크릿 비치로 안내합니다.",
        note: "완전 프라이빗, 드론 촬영 포함.",
        mapQuery: "Yuni no Hama Miyakojima",
      },
      {
        time: "13:30",
        category: "beach",
        title: "조용한 비치에서 휴식",
        detail: "오후는 여유롭게 바다의 여운을 즐깁니다.",
        note: "저녁은 천천히 보내는 것을 추천합니다.",
        mapQuery: "Miyakojima quiet beach",
      },
    ],
  },
  cafe: {
    title: "카페 투어와 섬 시간",
    theme: "사진 찍기 좋은 카페를 돌며 여유롭게 보내는 날.",
    items: [
      {
        time: "10:30",
        category: "cafe",
        title: "Seahorse club cafe",
        detail: "사진으로 남기고 싶은 분위기의 카페입니다.",
        note: "오전부터 이른 오후가 촬영하기 좋습니다.",
        mapQuery: "Seahorse club cafe Miyakojima",
      },
      {
        time: "14:00",
        category: "cafe",
        title: "OHAMA Terrace / Pani Pani",
        detail: "바다나 남국 분위기의 카페를 한 곳 더 방문합니다.",
        note: "가까운 지역끼리 묶으면 이동이 편합니다.",
        mapQuery: "OHAMA Terrace Miyakojima",
      },
    ],
  },
  dinner: {
    title: "미야코지마 저녁과 밤",
    theme: "로컬 음식과 섬의 밤을 즐기는 날.",
    items: [
      {
        time: "18:30",
        category: "dinner",
        title: "추천 저녁 식당",
        detail:
          "Takara / Churashima / Deiriguchi / Umarizuma / Yakiniku Kihachi.",
        note: "인기 식당은 미리 예약하는 것이 좋습니다.",
        mapQuery: "Miyakojima dinner popular",
      },
      {
        time: "20:30",
        category: "night",
        title: "Muscle Bar / Kajitsuya",
        detail: "바나 밤 카페에서 미야코지마의 밤을 즐깁니다.",
        note: "다음 날 일정에 무리가 없게 가볍게 즐기세요.",
        mapQuery: "Muscle Bar Miyakojima",
      },
    ],
  },
  night: {
    title: "미야코지마 밤 즐기기",
    theme: "바와 밤 카페에서 미야코지마의 밤을 가볍게 즐기는 날.",
    items: [
      {
        time: "20:00",
        category: "night",
        title: "Muscle Bar",
        detail: "여행의 밤을 조금 더 즐기고 싶은 분께 추천합니다.",
        note: "다음 날 일정에 무리 없게 즐기세요.",
        mapQuery: "Muscle Bar Miyakojima",
      },
      {
        time: "21:30",
        category: "night",
        title: "Kajitsuya",
        detail: "저녁 식사 후 가볍게 들르기 좋은 밤 카페 느낌의 장소.",
        note: "짧게 들르는 코스로 좋습니다.",
        mapQuery: "Kajitsuya Miyakojima",
      },
    ],
  },
  star: {
    title: "별 촬영의 밤",
    theme: "구름과 달빛을 보며 별이 잘 보이는 장소로 이동합니다.",
    items: [
      {
        time: "20:30",
        category: "star",
        title: "별 촬영",
        detail: "가로등이 적은 해변이나 탁 트인 곳에서 촬영합니다.",
        note: "여행 초반에 넣으면 날씨에 따라 조정하기 쉽습니다.",
        mapQuery: "Miyakojima starry sky spot",
      },
    ],
  },
  beach: {
    title: "한적한 비치에서 쉬기",
    theme: "관광객이 적은 조용한 해변을 둘러보는 날.",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "조용한 로컬 비치",
        detail: "관광객이 잘 가지 않는 한적한 해변을 추천합니다.",
        note: "조수와 바람에 따라 가장 예쁜 장소가 달라집니다.",
        mapQuery: "Miyakojima hidden beach",
      },
    ],
  },
  secretBeach: {
    title: "시크릿 비치 투어",
    theme: "관광객이 적은 숨은 해변을 둘러보는 날.",
    items: [
      {
        time: "10:00",
        category: "beach",
        title: "시크릿 비치",
        detail: "조수와 바람에 맞춰 조용한 해변을 선택합니다.",
        note: "정확한 장소는 안전과 당일 컨디션에 따라 정하는 것이 좋습니다.",
        mapQuery: "Miyakojima secret beach",
      },
    ],
  },
  rentacar: {
    title: "렌터카 섬 드라이브",
    theme: "다리와 해안도로를 따라 자유롭게 미야코지마를 즐기는 날.",
    items: [
      {
        time: "09:30",
        category: "rest",
        title: "렌터카 절경 드라이브",
        detail:
          "이라부대교, 이케마대교, 쿠리마대교와 해안 전망지를 둘러봅니다.",
        note: "지역별로 묶으면 이동이 편합니다.",
        mapQuery: "Miyakojima scenic drive",
      },
    ],
  },
  rest: {
    title: "섬 드라이브",
    theme: "빡빡한 일정 대신 미야코지마다운 여유를 즐기는 날.",
    items: [
      {
        time: "10:00",
        category: "rest",
        title: "미야코지마 절경 드라이브",
        detail: "다리와 해안도로를 달리며 마음에 드는 곳에서 사진을 찍습니다.",
        note: "여유 있는 하루가 있으면 여행 전체가 편해집니다.",
        mapQuery: "Miyakojima scenic drive",
      },
    ],
  },
};

const latestLocalizedPlans = {
  EN: {
    yuni: {
      title: "Yuni no Hama Tour",
      theme: "Visit the floating sandbar at the best time for the tide.",
      items: [
        {
          title: "Yuni no Hama Tour",
          detail: "Travel by boat or marine activity to the sandbar that appears at low tide.",
          note: "Departure time and operation depend on tides and sea conditions, so advance booking is recommended.",
          bookingLabel: "Book a Yuni no Hama tour",
        },
        {
          title: "Island Cafe Lunch",
          detail: "Have an easy lunch and break at a nearby cafe after the tour.",
          note: "Bring a change of clothes that is easy to use after getting wet.",
        },
      ],
    },
    yaebiji: {
      title: "Explore the Yabiji Reefs",
      theme: "Take a boat to Yabiji for snorkeling and underwater photography.",
      items: [
        {
          title: "Yabiji Tour",
          detail: "Snorkel and take underwater photos among one of Japan's largest coral reef groups.",
          note: "The tour takes about 4–5 hours. Locations and operation depend on sea conditions.",
          bookingLabel: "Book a Yabiji tour",
        },
        {
          title: "Photogenic cafes",
          detail: "After the ocean, choose a cafe from the list and take a relaxed break.",
          note: "Pick one based on your location and mood that day.",
          showCafeOptions: true,
        },
      ],
    },
    rest: {
      title: "Ikema Island Drive",
      theme: "Cross Ikema Bridge and explore beaches, cafes, and island treats at a relaxed pace.",
      items: [
        { title: "Photo Stop at Ikema Bridge", detail: "Photograph the Ikema-blue sea from a safe viewpoint near the bridge.", note: "Do not stop on the bridge; use a safe parking area.", categoryLabel: "Drive" },
        { title: "Heart Rock & Ikizu Beach", detail: "See the heart-shaped rock and a quiet beach on Ikema Island.", note: "The view changes with the tide." },
        { title: "Funakusu Beach", detail: "Take a relaxed walk beside the exceptionally clear water.", note: "Check sea conditions before swimming." },
        { title: "Kaimirai", detail: "Try Ikema Island's famous purple sweet-potato rice cake.", note: "It can sell out, so an earlier visit is recommended.", categoryLabel: "Sweets" },
        { title: "Gelato cafe Ninufa", detail: "Enjoy island-ingredient gelato while looking over the sea.", note: "All seats overlook the ocean terrace." },
        { title: "OHAMA Terrace", detail: "Finish the drive with a relaxing break on a terrace overlooking the sea.", note: "Check opening days and hours on Instagram before visiting." },
      ],
    },
  },
  中文: {
    yuni: {
      title: "Yuni沙洲之旅",
      theme: "配合潮汐时间，前往浮在海上的梦幻沙洲。",
      items: [
        { title: "Yuni沙洲之旅", detail: "搭船或参加海上活动，前往退潮时出现的Yuni沙洲。", note: "出发时间和是否成行会受潮位与海况影响，建议提前预约。", bookingLabel: "预约Yuni沙洲行程" },
        { title: "岛上咖啡午餐", detail: "行程结束后，在附近咖啡店轻松享用午餐和休息。", note: "建议准备方便更换的衣服。" },
      ],
    },
    yaebiji: {
      title: "八重干濑珊瑚礁之旅",
      theme: "搭船前往八重干濑，体验浮潜和水下拍摄。",
      items: [
        { title: "八重干濑行程", detail: "在日本规模最大的珊瑚礁群之一体验浮潜和水下摄影。", note: "所需时间约4～5小时，地点和是否成行会受海况影响。", bookingLabel: "预约八重干濑行程" },
        { title: "拍照咖啡店", detail: "海上活动后，从列表中选择想去的咖啡店悠闲休息。", note: "可根据当天位置和心情选择。", showCafeOptions: true },
      ],
    },
    rest: {
      title: "池间岛自驾",
      theme: "穿过池间大桥，轻松游览海滩、咖啡店和岛上美食。",
      items: [
        { title: "池间大桥拍摄", detail: "在安全的观景地点拍摄池间蓝海景。", note: "不要在桥上停车，请使用安全停车区。", categoryLabel: "自驾" },
        { title: "心形岩＆Ikizu海滩", detail: "欣赏心形岩石和池间岛安静的海滩。", note: "景色会随潮位变化。" },
        { title: "Funakusu海滩", detail: "在透明度很高的海边悠闲散步。", note: "下水前请确认海况。" },
        { title: "海美来", detail: "品尝池间岛名物红芋糯米饼。", note: "有时会售罄，建议早点前往。", categoryLabel: "甜点" },
        { title: "Gelato cafe Ninufa", detail: "一边欣赏池间岛海景，一边享用岛产食材冰淇淋。", note: "所有座位都能看到海景露台。" },
        { title: "OHAMA Terrace", detail: "在海景露台休息，为自驾行程收尾。", note: "前往前请在Instagram确认营业日和时间。" },
      ],
    },
  },
  한국어: {
    yuni: {
      title: "유니노하마 투어",
      theme: "물때에 맞춰 바다 위에 떠오르는 환상의 모래섬을 즐깁니다.",
      items: [
        { title: "유니노하마 투어", detail: "보트나 해양 액티비티로 썰물 때 나타나는 유니노하마로 이동합니다.", note: "조수와 해상 상태에 따라 출발 시간과 진행 내용이 달라져 사전 예약을 추천합니다.", bookingLabel: "유니노하마 투어 예약" },
        { title: "섬 카페 점심", detail: "투어 후 가까운 카페에서 여유롭게 점심과 휴식을 즐깁니다.", note: "젖은 뒤 갈아입기 편한 옷을 준비하면 좋습니다." },
      ],
    },
    yaebiji: {
      title: "야에비지 산호초 투어",
      theme: "보트를 타고 야에비지로 이동해 스노클링과 수중 촬영을 즐깁니다.",
      items: [
        { title: "야에비지 투어", detail: "일본 최대급 산호초 군락에서 스노클링과 수중 촬영을 체험합니다.", note: "약 4~5시간 소요되며 해상 상태에 따라 포인트와 진행 여부가 달라집니다.", bookingLabel: "야에비지 투어 예약" },
        { title: "사진 예쁜 카페", detail: "바다 체험 후에는 목록에서 가고 싶은 카페를 골라 여유롭게 쉽니다.", note: "이동 위치와 그날의 기분에 맞춰 고를 수 있습니다.", showCafeOptions: true },
      ],
    },
    rest: {
      title: "이케마섬 드라이브",
      theme: "이케마대교를 건너 비치, 카페, 섬 간식을 여유롭게 둘러봅니다.",
      items: [
        { title: "이케마대교 촬영", detail: "안전한 전망 포인트에서 이케마 블루 바다를 촬영합니다.", note: "다리 위에는 정차하지 말고 안전한 주차 공간을 이용하세요.", categoryLabel: "드라이브" },
        { title: "하트 바위 & 이키즈 비치", detail: "하트 모양 바위와 이케마섬의 조용한 비치를 즐깁니다.", note: "조수에 따라 보이는 모습이 달라집니다." },
        { title: "후나쿠스 비치", detail: "투명한 바다를 바라보며 비치를 천천히 산책합니다.", note: "수영 전에는 해상 상태를 확인하세요." },
        { title: "카이미라이", detail: "이케마섬 명물 자색 고구마 떡을 맛봅니다.", note: "품절될 수 있어 이른 시간 방문을 추천합니다.", categoryLabel: "디저트" },
        { title: "Gelato cafe Ninufa", detail: "이케마섬 바다를 보며 섬 재료 젤라토로 휴식합니다.", note: "모든 좌석에서 오션뷰 테라스를 즐길 수 있습니다." },
        { title: "OHAMA Terrace", detail: "바다가 보이는 테라스에서 드라이브의 마지막 휴식을 즐깁니다.", note: "방문 전 Instagram에서 영업일과 시간을 확인하세요." },
      ],
    },
  },
};

Object.entries(latestLocalizedPlans).forEach(([lang, translatedPlans]) => {
  Object.entries(translatedPlans).forEach(([key, translatedPlan]) => {
    const sourcePlan = localizedPlans.JP[key];
    localizedPlans[lang][key] = {
      ...sourcePlan,
      title: translatedPlan.title,
      theme: translatedPlan.theme,
      items: sourcePlan.items.map((item, index) => ({
        ...item,
        mediaKey: item.title,
        ...translatedPlan.items[index],
      })),
    };
  });
});

const sharedPlanTranslations = {
  EN: {
    arrival: [
      ["Miyako Shimojishima Airport", "Take a short break at the airport before picking up the rental car.", "Prepare drinks and sunscreen here before setting out."],
      ["51 Rent-a-Car Shimoji", "Pick up your rental car and begin your Miyakojima trip.", "96-20 Irabu Nakachi, Miyakojima / Tel. 0980-79-9951"],
      ["Blue Turtle", "Relax while looking over Toguchi Beach and the sea of Irabu Island.", "Take a break at this ocean-view cafe."],
      ["17END", "A scenic spot where you can see the brilliant Miyako blue.", "Visiting around low tide is recommended."],
    ],
    departure: [
      ["Hotel checkout", "Check your room and check out of the hotel.", "Load your luggage into the car, then head out for breakfast."],
      ["Breakfast", "Pick up breakfast before heading to the airport.", ""],
      ["To Miyako Shimojishima Airport", "Return the rental car and head to the airport.", "Adjust the departure time to suit your flight."],
    ],
    cafe: [
      ["Pani Pani", "Start the morning at a tropical cafe surrounded by greenery on Kurima Island.", ""],
      ["Shimajaya Yakkayakka", "Enjoy a rich Miyako beef curry on Kurima Island.", "It is close to Pani Pani and easy to visit in the same route."],
      ["SHISA'S CAFE & BBQ", "Watch the sunset while looking over the sea.", "Arriving around 18:30 is recommended for sunset."],
    ],
    planMeta: {
      cafe: ["Cafe Hopping and Island Time", "Spend a relaxed day visiting cafes recommended for photography."],
      photo: ["Hidden Scenic Photo Tour", "Capture photos and video while visiting hidden and scenic locations."],
      sup: ["Private Hidden SUP", "Enjoy SUP and photography in exceptionally clear water."],
      star: ["Star Photography", "Travel to a dark location with no streetlights for star photography."],
    },
    photoFirst: ["Scenic Photo Tour", "Visit Miyakojima's scenic locations, hidden spots, and secret beaches.", "Drone and mirrorless-camera photography included.", "Scenic photo tour details"],
    supFirst: ["Hidden SUP Tour", "Visit a quiet point selected for the day's sea conditions.", "Includes photos, video, and drone photography.", "Hidden SUP tour details"],
    starFirst: ["Star Photography", "7,000 yen per person. Photography takes place somewhere completely dark with no streetlights.", "Meet on site. Pickup is available for an additional 2,000 yen per group.", "Book via LINE"],
  },
  中文: {
    arrival: [["宫古下地岛机场", "到达后，在取租车前于机场稍作休息。", "建议在这里准备饮料和防晒用品。"], ["51租车 下地店", "取车后开始宫古岛之旅。", "宫古岛市伊良部仲地96-20 / 电话 0980-79-9951"], ["Blue Turtle", "眺望伊良部岛渡口之滨，悠闲休息。", "在海景咖啡店稍作休息。"], ["17END", "可以欣赏宫古蓝的绝景景点。", "推荐在退潮时前往。"]],
    departure: [["酒店退房", "确认没有遗漏物品后办理退房。", "把行李放进车里，再去吃早餐。"], ["早餐", "前往机场前领取早餐。", ""], ["前往宫古下地岛机场", "归还租车后前往机场。", "请根据航班时间调整出发时间。"]],
    cafe: [["Pani Pani", "在来间岛绿意环绕的咖啡店享受南国早晨。", ""], ["岛茶家Yakkayakka", "在来间岛品尝浓郁的宫古牛咖喱。", "距离Pani Pani很近，可轻松顺路前往。"], ["SHISA'S CAFE & BBQ", "眺望大海，欣赏傍晚日落。", "推荐18:30左右抵达。"]],
    planMeta: { cafe: ["咖啡巡游与岛屿时光", "轻松巡游适合拍摄的咖啡店。"], photo: ["秘境绝景拍摄之旅", "巡游秘境与绝景，留下旅途照片和视频。"], sup: ["秘境SUP体验", "在清澈海水中体验SUP与拍摄。"], star: ["星空拍摄", "前往没有路灯的黑暗地点拍摄星空。"] },
    photoFirst: ["绝景拍摄之旅", "巡游宫古岛绝景、秘境和秘密海滩并进行拍摄。", "包含无人机和相机拍摄。", "查看秘境拍摄详情"],
    supFirst: ["秘境SUP之旅", "根据当天海况前往游客较少的地点。", "包含照片、视频和无人机拍摄。", "查看秘境SUP详情"],
    starFirst: ["星空拍摄", "每人7,000日元。在没有路灯的黑暗地点拍摄。", "现场集合。接送每组另加2,000日元。", "通过LINE预约"],
  },
  한국어: {
    arrival: [["미야코 시모지시마 공항", "도착 후 렌터카를 받기 전 공항에서 잠시 쉽니다.", "음료와 선크림을 미리 준비하면 좋습니다."], ["51 렌터카 시모지점", "렌터카를 빌려 미야코지마 여행을 시작합니다.", "미야코지마시 이라부 나카치 96-20 / 전화 0980-79-9951"], ["Blue Turtle", "이라부섬 도구치 해변을 바라보며 여유롭게 쉽니다.", "바다가 보이는 카페에서 휴식합니다."], ["17END", "미야코 블루를 볼 수 있는 절경 포인트입니다.", "썰물 시간에 방문하는 것을 추천합니다."]],
    departure: [["호텔 체크아웃", "빠진 물건이 없는지 확인하고 체크아웃합니다.", "짐을 차에 싣고 아침 식사를 하러 이동합니다."], ["아침 식사", "공항으로 가기 전 아침 식사를 픽업합니다.", ""], ["미야코 시모지시마 공항으로", "렌터카를 반납하고 공항으로 이동합니다.", "항공편 시간에 맞춰 출발 시간을 조정하세요."]],
    cafe: [["Pani Pani", "쿠리마섬의 녹음 속 카페에서 남국의 아침을 즐깁니다.", ""], ["시마자야 얏카얏카", "쿠리마섬에서 진한 미야코규 카레를 즐깁니다.", "Pani Pani와 가까워 함께 방문하기 좋습니다."], ["SHISA'S CAFE & BBQ", "바다를 바라보며 저녁 노을을 즐깁니다.", "일몰에 맞춰 18:30경 도착을 추천합니다."]],
    planMeta: { cafe: ["카페 투어와 섬 시간", "촬영하기 좋은 카페를 둘러보며 여유롭게 보냅니다."], photo: ["비경 절경 촬영 투어", "비경과 절경 포인트를 돌며 여행 사진과 영상을 남깁니다."], sup: ["비경 SUP 체험", "맑은 바다에서 SUP와 촬영을 즐깁니다."], star: ["별 사진 촬영", "가로등이 없는 어두운 장소에서 별을 촬영합니다."] },
    photoFirst: ["절경 촬영 투어", "미야코지마의 절경, 비경, 시크릿 비치를 돌며 촬영합니다.", "드론과 미러리스 카메라 촬영 포함.", "비경 촬영 투어 상세"],
    supFirst: ["비경 SUP 투어", "당일 해상 상태에 맞춰 관광객이 적은 포인트로 이동합니다.", "사진, 영상, 드론 촬영 포함.", "비경 SUP 상세"],
    starFirst: ["별 사진 촬영", "1인 7,000엔. 가로등이 없는 완전히 어두운 장소에서 촬영합니다.", "현지 집합. 픽업은 그룹당 2,000엔 추가입니다.", "LINE으로 예약"],
  },
};

Object.entries(sharedPlanTranslations).forEach(([lang, translated]) => {
  localizedPlans[lang].arrival = localizedPlans.JP.arrival.map((item, index) => ({
    ...item, mediaKey: item.title, title: translated.arrival[index][0], detail: translated.arrival[index][1], note: translated.arrival[index][2],
  }));
  localizedPlans[lang].departure = localizedPlans.JP.departure.map((item, index) => ({
    ...item, mediaKey: item.title, title: translated.departure[index][0], detail: translated.departure[index][1], note: translated.departure[index][2],
  }));
  localizedPlans[lang].cafe = { ...localizedPlans.JP.cafe, title: translated.planMeta.cafe[0], theme: translated.planMeta.cafe[1], items: localizedPlans.JP.cafe.items.map((item, index) => ({ ...item, mediaKey: item.title, title: translated.cafe[index][0], detail: translated.cafe[index][1], note: translated.cafe[index][2] })) };
  [["photo", "photoFirst"], ["sup", "supFirst"], ["star", "starFirst"]].forEach(([key, firstKey]) => {
    const source = localizedPlans.JP[key];
    const first = translated[firstKey];
    localizedPlans[lang][key] = { ...localizedPlans[lang][key], title: translated.planMeta[key][0], theme: translated.planMeta[key][1], items: source.items.map((item, index) => index === 0 ? ({ ...item, mediaKey: item.title, title: first[0], detail: first[1], note: first[2], detailLabel: item.detailUrl ? first[3] : undefined, bookingLabel: item.bookingUrl ? first[3] : undefined }) : ({ ...localizedPlans[lang][key].items[index], mediaKey: item.title })) };
  });
  localizedPlans[lang].dinner.items[0].mediaKey = localizedPlans.JP.dinner.items[0].title;
});

const planOrder = [
  "photo",
  "drone",
  "sup",
  "yuni",
  "yaebiji",
  "drive",
  "cafe",
  "dinner",
  "night",
  "star",
  "secretBeach",
];
const defaultInterests = ["photo", "sup", "cafe"];
const moodOrder = ["healing", "packed", "shootSlow", "selfDrive", "rainy"];
const moodInterestMap = {
  healing: ["sup", "secretBeach", "cafe", "star"],
  packed: ["photo", "sup", "yaebiji", "cafe", "dinner", "night", "star"],
  shootSlow: ["photo", "drone", "cafe", "secretBeach", "star"],
  selfDrive: ["rentacar", "drive", "cafe", "dinner"],
  rainy: ["cafe"],
};

const getPlans = (lang) => localizedPlans[lang] || localizedPlans.JP;

const itemImages = {
  arrival: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp"),
  departure: img("v1785003708/DJI_20260707132223_0047_D_small_no9d5s.jpg"),
  tour: img("v1784766584/DJI_20260721090315_0012_D_nm08ha.webp"),
  cafe: img("v1784466423/DJI_20260718160638_0015_D_gh8w0n.webp"),
  dinner: img("v1785005395/DSC00157_small_rnomp6.jpg"),
  lunch: img("v1785005395/DSC00157_small_rnomp6.jpg"),
  beach: img("v1784766584/DJI_20260722152001_0021_D_small_odgfk0.jpg"),
  night: img("v1784531285/IMG_1066_sd05qe.webp"),
  star: "https://res.cloudinary.com/drxijtp8g/image/upload/v1784266422/DSC00400-2_ddg5if.jpg",
  rest: img("v1783928437/DJI_20260707115907_0008_D_xag4ot.webp"),
};

const getItemImage = (item) => itemImages[item.category] || itemImages.rest;

const tourSchedules = {
  JP: {
    photo: [
      ["9:30", "お迎え"],
      ["10:00", "秘境スポットで撮影"],
      ["11:30", "絶景ポイントを巡る"],
      ["12:30", "写真・映像確認"],
    ],
    sup: [
      ["8:30", "集合"],
      ["9:00", "秘境スポットへSUPで移動・撮影"],
      ["10:30", "写真・映像確認"],
      ["11:00", "解散"],
    ],
    jet: [
      ["10:00", "現地集合"],
      ["10:30", "JETで秘境へ"],
      ["11:10", "ユニの浜で撮影"],
      ["12:00", "シークレットビーチへ"],
      ["13:00", "現地解散"],
    ],
  },
  EN: {
    photo: [
      ["9:30", "Pickup"],
      ["10:00", "Hidden spot shooting"],
      ["11:30", "Scenic spots"],
      ["12:30", "Photo and video check"],
    ],
    sup: [
      ["8:30", "Meet"],
      ["9:00", "Travel by SUP to a hidden spot and shoot"],
      ["10:30", "Photo and video check"],
      ["11:00", "Finish"],
    ],
    jet: [
      ["10:00", "Meet on site"],
      ["10:30", "Ride to hidden areas"],
      ["11:10", "Yuni no Hama shooting"],
      ["12:00", "Secret beach"],
      ["13:00", "Finish on site"],
    ],
  },
  中文: {
    photo: [
      ["9:30", "接送"],
      ["10:00", "秘境地点拍摄"],
      ["11:30", "巡游绝景点"],
      ["12:30", "确认照片和视频"],
    ],
    sup: [
      ["8:30", "集合"],
      ["9:00", "乘SUP前往秘境并拍摄"],
      ["10:30", "确认照片和视频"],
      ["11:00", "结束"],
    ],
    jet: [
      ["10:00", "现场集合"],
      ["10:30", "乘JET前往秘境"],
      ["11:10", "Yuni no Hama拍摄"],
      ["12:00", "秘境海滩"],
      ["13:00", "现场解散"],
    ],
  },
  한국어: {
    photo: [
      ["9:30", "픽업"],
      ["10:00", "숨은 장소 촬영"],
      ["11:30", "절경 포인트 이동"],
      ["12:30", "사진・영상 확인"],
    ],
    sup: [
      ["8:30", "집합"],
      ["9:00", "SUP로 비경 포인트 이동・촬영"],
      ["10:30", "사진・영상 확인"],
      ["11:00", "종료"],
    ],
    jet: [
      ["10:00", "현지 집합"],
      ["10:30", "JET로 숨은 곳 이동"],
      ["11:10", "유니의 하마 촬영"],
      ["12:00", "시크릿 비치"],
      ["13:00", "현지 해산"],
    ],
  },
};

const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes) => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const rest = normalized % 60;
  return `${hours}:${String(rest).padStart(2, "0")}`;
};

const getTourSchedule = (item, lang) => {
  const schedule =
    tourSchedules[lang]?.[item.tourType] || tourSchedules.JP[item.tourType] || [];

  if (!schedule.length || !item.time || item.time === schedule[0][0]) {
    return schedule;
  }

  const offset = timeToMinutes(item.time) - timeToMinutes(schedule[0][0]);
  return schedule.map(([time, label]) => [
    minutesToTime(timeToMinutes(time) + offset),
    label,
  ]);
};

const localizePlan = (plan, t, index) => ({
  day: index + 1,
  date: `Day ${index + 1}`,
  title: plan.title,
  theme: plan.theme,
  items: plan.items.map((item, itemIndex) => ({
    ...item,
    id: `${index + 1}-${itemIndex + 1}-${item.title}`,
    categoryLabel:
      item.categoryLabel || t.labels[item.category] || item.category,
  })),
});

const buildItinerary = ({
  nights,
  interests,
  lang,
  breakfastIndex = 0,
  inputMode = "interests",
  mood = "",
}) => {
  const t = text[lang] || text.JP;
  const plans = getPlans(lang);
  const totalDays = Number(nights) + 1;
  const selected = interests;
  const localizedBreakfast =
    breakfastTranslations[lang]?.[breakfastIndex % breakfastOptions.length];
  const departureItems = plans.departure.map((item, index) =>
    index === 1
      ? {
          ...breakfastOptions[breakfastIndex % breakfastOptions.length],
          ...localizedBreakfast,
          time: "09:30",
        }
      : item,
  );
  const experienceKeys = [
    ...new Set(
      planOrder
        .filter((key) => selected.includes(key))
        .map((key) =>
          key === "drone"
            ? "photo"
            : key === "secretBeach"
              ? "beach"
            : key === "drive" || key === "rentacar"
              ? "rest"
              : key,
        ),
    ),
  ];
  const middleDayCount = Math.max(totalDays - 2, 0);
  const photoOnArrival =
    totalDays > 2 &&
    experienceKeys.includes("photo") &&
    experienceKeys.includes("cafe");
  const cafeOnArrival =
    totalDays > 2 && experienceKeys.includes("cafe") && !photoOnArrival;
  const keysAfterArrival = experienceKeys.filter(
    (key) =>
      !(photoOnArrival && key === "photo") &&
      !(photoOnArrival && key === "cafe") &&
      !(cafeOnArrival && key === "cafe"),
  );
  const starOnArrival =
    totalDays > 2 &&
    keysAfterArrival.includes("star") &&
    keysAfterArrival.filter((key) => key !== "star").length >=
      middleDayCount * 2;
  let middleExperienceKeys = starOnArrival
    ? keysAfterArrival.filter((key) => key !== "star")
    : keysAfterArrival;
  let arrivalExtraKeys = [];
  const daytimeTourKeys = ["photo", "sup", "yuni", "yaebiji"];

  if (totalDays > 2 && !photoOnArrival && !cafeOnArrival && middleDayCount > 0) {
    const selectedDaytimeTours = middleExperienceKeys.filter((key) =>
      daytimeTourKeys.includes(key),
    );
    if (selectedDaytimeTours.length > middleDayCount) {
      arrivalExtraKeys = selectedDaytimeTours.slice(middleDayCount);
      middleExperienceKeys = middleExperienceKeys.filter(
        (key) => !arrivalExtraKeys.includes(key),
      );
    }
  }

  const getExperienceItem = (plan) =>
    plan.items.find((item) => item.category === "tour") || plan.items[0];

  const arrangeItems = (keys, firstTourTime = "09:00") => {
    let daytimeIndex = 0;
    const daytimeSlots =
      firstTourTime === "13:00"
        ? ["13:00", "17:30", "20:30"]
        : [firstTourTime, "14:30", "18:30"];
    return keys.flatMap((key) => {
      const plan = plans[key] || localizedPlans.JP[key] || plans.rest;

      if (key === "rest") {
        return plan.items;
      }

      const item = getExperienceItem(plan);
      let time;

      if (item.tourType === "sup") {
        time = firstTourTime === "13:00" ? "13:00" : "09:00";
      }
      else if (item.category === "dinner") time = "19:00";
      else if (item.category === "night") time = "20:30";
      else if (item.category === "star") time = "21:00";
      else if (item.category === "cafe") time = "16:30";
      else {
        time = daytimeSlots[Math.min(daytimeIndex, daytimeSlots.length - 1)];
        daytimeIndex += 1;
      }

      return { ...item, time };
    }).sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  };

  const tourItem = (key, time) => ({
    ...getExperienceItem(plans[key] || localizedPlans.JP[key] || plans.rest),
    time,
  });
  const dinnerItem = (time = "18:30") => ({
    ...plans.dinner.items[0],
    time,
  });
  const hotelDinnerItem = (time = "18:30") => ({
    time,
    category: "dinner",
    title: t.customPlans.hotelDinnerTitle,
    detail: t.customPlans.hotelDinnerDetail,
    note: t.customPlans.hotelDinnerNote,
    mapQuery: "宮古島 ホテル レストラン",
    mediaKey: "ホテルでゆっくり夜ご飯",
    hotelDinnerOptions: true,
  });
  const hotelRelaxItem = (time = "16:30") => ({
    time,
    category: "rest",
    title: t.customPlans.hotelRelaxTitle,
    detail: t.customPlans.hotelRelaxDetail,
    note: t.customPlans.hotelDinnerNote,
    mapQuery: "宮古島 ホテル",
    hotelOptions: true,
  });
  const rainyIndoorItem = (time = "13:00") => ({
    time,
    category: "rest",
    title: t.customPlans.rainyIndoorTitle,
    detail: t.customPlans.rainyIndoorDetail,
    note: t.customPlans.rainyIndoorNote,
    mapQuery: "宮古島 雨の日 室内体験",
    mediaKey: "雨の日の室内体験",
    detailUrl: "/blog/rainy-day-plan",
    detailLabel: t.customPlans.rainyIndoorDetailLabel,
  });
  const photogenicCafeItem = (time = "13:00") => ({
    time,
    category: "cafe",
    title: t.customPlans.photogenicCafeTitle,
    detail: t.customPlans.photogenicCafeDetail,
    note: t.customPlans.photogenicCafeNote,
    mapQuery: "宮古島 映えるカフェ",
    showCafeOptions: true,
  });
  const rainyCafeItem = (time = "14:30") => ({
    ...photogenicCafeItem(time),
    title: t.customPlans.rainyCafeTitle,
    detail: t.customPlans.rainyCafeDetail,
    note: t.customPlans.rainyCafeNote,
  });
  const nightItem = (time = "20:30") => ({
    ...(plans.dinner.items[1] || plans.night.items[0]),
    time,
  });
  const starItem = (time = "20:30") => ({
    ...getExperienceItem(plans.star),
    time,
  });
  const departurePlan = () =>
    localizePlan(
      {
        title: departureItems[0].title,
        theme: t.departureTheme,
        items: departureItems,
      },
      t,
      totalDays - 1,
    );

  const buildHealingMoodItinerary = () => {
    if (totalDays === 2) {
      return [
        localizePlan(
          {
            title: t.arrivalTitle,
            theme: plans.sup.theme,
            items: [
              ...plans.arrival.slice(0, 2),
              tourItem("sup", "13:00"),
              hotelDinnerItem("18:30"),
            ],
          },
          t,
          0,
        ),
        departurePlan(),
      ];
    }

    const middlePlansForMood = [
      {
        title: plans.sup.title,
        theme: plans.sup.theme,
        items:
          totalDays === 3
            ? [
                ...plans.sup.items.map((item) =>
                  item.tourType === "sup" ? { ...item, time: "09:00" } : item,
                ),
                hotelDinnerItem("18:30"),
                starItem("20:30"),
              ]
            : plans.sup.items.map((item) =>
                item.tourType === "sup" ? { ...item, time: "09:00" } : item,
              ),
      },
    ];

    if (totalDays >= 4) {
      middlePlansForMood.push({
        title: t.customPlans.hotelStarTitle,
        theme: t.customPlans.hotelStarTheme,
        items: [hotelDinnerItem("18:30"), starItem("20:30")],
      });
    }

    while (middlePlansForMood.length < totalDays - 2) {
      middlePlansForMood.push(
        middlePlansForMood.length % 2 === 0 ? plans.cafe : plans.rest,
      );
    }

    return [
      localizePlan(
        {
          title: t.arrivalTitle,
          theme: plans.arrival[1].detail,
          items: [...plans.arrival, hotelDinnerItem("18:30")],
        },
        t,
        0,
      ),
      ...middlePlansForMood
        .slice(0, totalDays - 2)
        .map((plan, index) => localizePlan(plan, t, index + 1)),
      departurePlan(),
    ];
  };

  const buildPackedMoodItinerary = () => {
    const arrivalPhotoDinner = localizePlan(
      {
        title: t.arrivalPhotoTitle,
        theme: plans.photo.theme,
        items: [
          ...plans.arrival.slice(0, 2),
          tourItem("photo", "13:00"),
          dinnerItem("18:30"),
        ],
      },
      t,
      0,
    );

    if (totalDays === 2) {
      return [arrivalPhotoDinner, departurePlan()];
    }

    const middlePlansForMood = [
      {
        title: `${plans.yaebiji.title} ＋ ${plans.dinner.title} ＋ ${plans.night.title}`,
        theme: "午前中は八重干瀬ツアーへ。夜は宮古島のグルメや夜遊びを満喫します。",
        items: [
          ...plans.yaebiji.items,
          dinnerItem("18:30"),
          nightItem("20:30"),
        ],
      },
    ];

    if (totalDays >= 4) {
      middlePlansForMood.push({
        title:
          totalDays === 6
            ? plans.sup.title
            : `${plans.sup.title} ＋ ${plans.star.title}`,
        theme:
          totalDays === 6
            ? plans.sup.theme
            : "日中は秘境SUP、夜は星空撮影まで楽しむ日。",
        items:
          totalDays === 6
            ? plans.sup.items.map((item) =>
                item.tourType === "sup" ? { ...item, time: "09:00" } : item,
              )
            : [
                ...plans.sup.items.map((item) =>
                  item.tourType === "sup" ? { ...item, time: "09:00" } : item,
                ),
                starItem("20:30"),
              ],
      });
    }

    if (totalDays >= 5) {
      middlePlansForMood.push({
        ...plans.yuni,
        items: plans.yuni.items.map((item) =>
          item.tourType === "yuni" ? { ...item, time: "09:30" } : item,
        ),
      });
    }

    if (totalDays === 6) {
      middlePlansForMood.push({
        title: `${plans.dinner.title} ＋ ${plans.star.title}`,
        theme: "最終日前の夜に、夜ご飯と星空撮影をゆっくり楽しみます。",
        items: [dinnerItem("18:30"), starItem("20:30")],
      });
    }

    while (middlePlansForMood.length < totalDays - 2) {
      middlePlansForMood.push(plans.cafe);
    }

    return [
      arrivalPhotoDinner,
      ...middlePlansForMood
        .slice(0, totalDays - 2)
        .map((plan, index) => localizePlan(plan, t, index + 1)),
      departurePlan(),
    ];
  };

  const irabuDrivePlan = {
    title: "伊良部島ドライブ",
    theme: "伊良部大橋を渡り、海が見えるカフェと絶景スポットを自由に巡る日。",
    items: [
      { ...plans.arrival[2], time: "10:30" },
      { ...plans.arrival[3], time: "12:30" },
      {
        time: "14:30",
        category: "beach",
        title: "渡口の浜",
        detail: "伊良部島らしい白砂と青い海をゆっくり楽しみます。",
        note: "風が強い日は波に注意しながら、写真だけでも十分きれいです。",
        mapQuery: "渡口の浜 宮古島",
      },
      {
        time: "16:30",
        category: "rest",
        categoryLabel: "ドライブ",
        title: "伊良部大橋で夕方ドライブ",
        detail: "宮古島へ戻りながら、橋の上から見える海の色を楽しみます。",
        note: "橋の上では停車せず、安全な場所で休憩してください。",
        mapQuery: "伊良部大橋 宮古島",
      },
    ],
  };

  const buildShootSlowMoodItinerary = () => {
    const includeStarOnArrival = totalDays === 2;
    const arrivalPhotoStar = localizePlan(
      {
        title: includeStarOnArrival
          ? `${plans.photo.title} ＋ ${plans.star.title}`
          : t.customPlans.shootArrivalTitle,
        theme: includeStarOnArrival
          ? t.customPlans.shootArrivalWithStarTheme
          : t.customPlans.shootArrivalTheme,
        items: [
          ...plans.arrival.slice(0, 2),
          tourItem("photo", "13:00"),
          ...(includeStarOnArrival ? [starItem("20:30")] : []),
        ],
      },
      t,
      0,
    );

    if (totalDays === 2) {
      return [arrivalPhotoStar, departurePlan()];
    }

    const middlePlansForMood = [];

    if (totalDays === 3) {
      middlePlansForMood.push({
        title: t.customPlans.yabijiStarTitle,
        theme: t.customPlans.yabijiStarTheme,
        items: [
          ...plans.yaebiji.items,
          photogenicCafeItem("15:00"),
          dinnerItem("18:30"),
          starItem("20:30"),
        ],
      });
    } else if (totalDays === 4) {
      middlePlansForMood.push(
        {
          title: plans.cafe.title,
          theme: plans.cafe.theme,
          items: plans.cafe.items,
        },
        {
          ...plans.sup,
          items: plans.sup.items.map((item) =>
            item.tourType === "sup" ? { ...item, time: "09:00" } : item,
          ),
        },
      );
    } else if (totalDays === 5) {
      middlePlansForMood.push(
        {
          title: `${plans.yuni.title} ＋ ${plans.star.title}`,
          theme: "朝はユニの浜ツアー、夜は星空撮影を楽しむ日。",
          items: [
            ...plans.yuni.items.map((item) =>
              item.tourType === "yuni" ? { ...item, time: "09:30" } : item,
            ),
            starItem("20:30"),
          ],
        },
        {
          title: `${plans.sup.title} ＋ ${plans.dinner.title}`,
          theme: "日中は秘境SUP、夜は宮古島の夜ご飯を楽しみます。",
          items: [
            ...plans.sup.items.map((item) =>
              item.tourType === "sup" ? { ...item, time: "09:00" } : item,
            ),
            dinnerItem("18:30"),
          ],
        },
        plans.rest,
      );
    } else {
      middlePlansForMood.push(
        {
          title: `${plans.yaebiji.title} ＋ ${plans.dinner.title}`,
          theme: "午前中は八重干瀬ツアーへ。夜は宮古島の夜ご飯を楽しみます。",
          items: [...plans.yaebiji.items, dinnerItem("18:30")],
        },
        {
          ...plans.sup,
          items: plans.sup.items.map((item) =>
            item.tourType === "sup" ? { ...item, time: "09:00" } : item,
          ),
        },
        plans.cafe,
        {
          title: plans.star.title,
          theme: plans.star.theme,
          items: [starItem("20:30")],
        },
      );
    }

    while (middlePlansForMood.length < totalDays - 2) {
      middlePlansForMood.push(plans.cafe);
    }

    return [
      arrivalPhotoStar,
      ...middlePlansForMood
        .slice(0, totalDays - 2)
        .map((plan, index) => localizePlan(plan, t, index + 1)),
      departurePlan(),
    ];
  };

  const buildSelfDriveMoodItinerary = () => {
    const middlePlansForMood =
      totalDays >= 5
        ? [plans.cafe, plans.rest, irabuDrivePlan]
        : [plans.cafe, plans.rest];

    while (middlePlansForMood.length < totalDays - 2) {
      middlePlansForMood.push(middlePlansForMood.length % 2 === 0 ? plans.cafe : plans.rest);
    }

    return [
      localizePlan(
        {
          title: t.arrivalCafeTitle,
          theme: plans.cafe.theme,
          items: [
            ...plans.arrival.slice(0, 2),
            ...plans.cafe.items.map((item, index) => ({
              ...item,
              time: ["11:30", "14:00", "18:30"][index] || "18:30",
            })),
          ],
        },
        t,
        0,
      ),
      ...middlePlansForMood
        .slice(0, totalDays - 2)
        .map((plan, index) => localizePlan(plan, t, index + 1)),
      departurePlan(),
    ];
  };

  const buildRainyMoodItinerary = () => {
    const rainyPlans = [
      {
        title: t.customPlans.rainyMainTitle,
        theme: t.customPlans.rainyMainTheme,
        items: [
          rainyIndoorItem("13:00"),
          rainyCafeItem("15:00"),
          hotelRelaxItem("18:00"),
        ],
      },
      {
        title: t.customPlans.rainyCafeHotelTitle,
        theme: t.customPlans.rainyCafeHotelTheme,
        items: [rainyCafeItem("11:30"), hotelRelaxItem("16:30")],
      },
      {
        title: t.customPlans.rainyIndoorPlanTitle,
        theme: t.customPlans.rainyIndoorPlanTheme,
        items: [rainyIndoorItem("10:30"), rainyCafeItem("14:30")],
      },
    ];

    if (totalDays === 2) {
      return [
        localizePlan(
          {
            title: t.customPlans.rainyArrivalTitle,
            theme: rainyPlans[0].theme,
            items: [...plans.arrival.slice(0, 2), ...rainyPlans[0].items],
          },
          t,
          0,
        ),
        departurePlan(),
      ];
    }

    return [
      localizePlan(
        {
          title: t.customPlans.rainyArrivalTitle,
          theme: rainyPlans[0].theme,
          items: [
            ...plans.arrival.slice(0, 2),
            rainyCafeItem("14:30"),
            hotelRelaxItem("18:00"),
          ],
        },
        t,
        0,
      ),
      ...rainyPlans
        .slice(0, totalDays - 2)
        .map((plan, index) => localizePlan(plan, t, index + 1)),
      departurePlan(),
    ];
  };

  if (inputMode === "mood" && mood === "healing") {
    return buildHealingMoodItinerary();
  }

  if (inputMode === "mood" && mood === "packed") {
    return buildPackedMoodItinerary();
  }

  if (inputMode === "mood" && mood === "shootSlow") {
    return buildShootSlowMoodItinerary();
  }

  if (inputMode === "mood" && mood === "selfDrive") {
    return buildSelfDriveMoodItinerary();
  }

  if (inputMode === "mood" && mood === "rainy") {
    return buildRainyMoodItinerary();
  }

  const dayAssignments = Array.from({ length: middleDayCount }, () => []);
  if (middleDayCount > 0) {
    const prioritizedKeys = [...middleExperienceKeys].sort(
      (a, b) => Number(b === "yaebiji") - Number(a === "yaebiji"),
    );
    prioritizedKeys.forEach((key, index) => {
      dayAssignments[index % middleDayCount].push(key);
    });
  }

  const middlePlans = dayAssignments.map((keys) => {
    if (keys.length === 0) return plans.rest;
    if (keys.length === 1) {
      const singlePlan =
        plans[keys[0]] || localizedPlans.JP[keys[0]] || plans.rest;
      if (keys[0] === "sup") {
        return {
          ...singlePlan,
          items: singlePlan.items.map((item) =>
            item.tourType === "sup" ? { ...item, time: "09:00" } : item,
          ),
        };
      }
      return singlePlan;
    }

    const orderedKeys = [...keys].sort((a, b) => {
      const aTime = arrangeItems([a])[0]?.time || "23:59";
      const bTime = arrangeItems([b])[0]?.time || "23:59";
      return timeToMinutes(aTime) - timeToMinutes(bTime);
    });
    const assignedPlans = orderedKeys.map(
      (key) => plans[key] || localizedPlans.JP[key] || plans.rest,
    );
    return {
      title: assignedPlans.map((plan) => plan.title).join(" ＋ "),
      theme:
        t.combinedTheme,
      items: arrangeItems(orderedKeys),
    };
  });

  if (totalDays === 2) {
    const selectedKey = experienceKeys[0];
    const mainPlan =
      plans[selectedKey] ||
      localizedPlans.JP[selectedKey] ||
      plans.rest;
    const afternoonExperience = arrangeItems(
      experienceKeys.length ? experienceKeys : ["rest"],
      "13:00",
    );
    const eveningDinner =
      experienceKeys.includes("yuni") &&
      !afternoonExperience.some((item) => item.category === "dinner")
        ? [
            {
              ...plans.dinner.items[0],
              time: "19:00",
            },
          ]
        : [];

    return [
      localizePlan(
        {
          title:
            t.arrivalTitle,
          theme: mainPlan.theme,
          items: [
            ...plans.arrival.slice(0, 2),
            ...afternoonExperience,
            ...eveningDinner,
          ],
        },
        t,
        0,
      ),
      localizePlan(
        {
          title: departureItems[0].title,
          theme: t.departureTheme,
          items: departureItems,
        },
        t,
        1,
      ),
    ];
  }

  return [
    localizePlan(
      {
        title:
          photoOnArrival
            ? t.arrivalPhotoTitle
            : cafeOnArrival
              ? t.arrivalCafeTitle
              : t.arrivalTitle,
        theme: photoOnArrival
          ? plans.photo.theme
          : cafeOnArrival
            ? plans.cafe.theme
            : plans.arrival[1].detail,
        items: [
          ...(photoOnArrival
            ? [
                ...plans.arrival.slice(0, 2),
                { ...getExperienceItem(plans.photo), time: "13:00" },
              ]
            : cafeOnArrival
            ? [
              ...plans.arrival.slice(0, 2),
              ...plans.cafe.items.map((item, index) => ({
                ...item,
                time: ["11:30", "14:00", "18:30"][index] || "18:30",
              })),
            ]
            : plans.arrival),
          ...(!photoOnArrival && !cafeOnArrival && arrivalExtraKeys.length
            ? arrangeItems(arrivalExtraKeys, "13:00")
            : []),
          ...(starOnArrival
            ? [{ ...getExperienceItem(plans.star), time: "21:00" }]
            : []),
        ].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time)),
      },
      t,
      0,
    ),
    ...middlePlans.map((plan, index) => localizePlan(plan, t, index + 1)),
    localizePlan(
      {
        title: departureItems[0].title,
        theme: t.departureTheme,
        items: departureItems,
      },
      t,
      totalDays - 1,
    ),
  ];
};

const TimelineCard = ({ item, index, previousItem, t, lang }) => {
  const isTour = item.category === "tour";
  const mediaKey = item.mediaKey || item.title;
  const hasLocalVideo = Boolean(localTourVideos[mediaKey]);
  const hasInstagramEmbed = Boolean(instagramPosts[mediaKey]);
  const hasInstagram = hasInstagramEmbed || hasLocalVideo;
  const schedule = isTour ? getTourSchedule(item, lang) : [];

  return (
    <article className={`${s.timelineRow} ${hasInstagram ? s.instagramRow : ""}`}>
      <div className={s.timeRail}>
        <span className={s.time}>{item.time}</span>
        <span className={s.node}>{index + 1}</span>
      </div>

      <div className={`${s.planCard} ${hasInstagram ? s.instagramCard : ""} ${item.hideMedia ? s.textOnlyCard : ""}`}>
        {item.hideMedia ? null : hasLocalVideo ? (
          <div className={s.instagramMedia}>
            <video
              src={localTourVideos[mediaKey]}
              controls
              playsInline
              preload="metadata"
              aria-label={`${item.title} ${t.videoLabel}`}
            />
          </div>
        ) : hasInstagram ? (
          <div className={s.instagramMedia}>
            <iframe
              src={instagramPosts[mediaKey]}
              title={`${item.title} Instagram`}
              loading="lazy"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              sandbox={
                item.title === "絶景撮影ツアー" ||
                item.title === "秘境SUPツアー"
                  ? "allow-scripts allow-same-origin allow-forms allow-presentation"
                  : undefined
              }
            />
          </div>
        ) : (
          <img
            className={s.cardImage}
            src={getItemImage(item)}
            alt=""
            loading="lazy"
          />
        )}

        <div className={s.cardBody}>
          <div className={s.cardTop}>
            <span
              className={`${s.categoryChip} ${
                s[`${item.category}Chip`] || s.restChip
              }`}
            >
              {item.categoryLabel}
            </span>
          </div>

          <h3>{item.title}</h3>
          <p className={s.detail}>{item.detail}</p>
          <p className={s.note}>{item.note}</p>

          {(item.showCafeOptions ||
            (item.category === "cafe" &&
              (item.title.includes("おすすめのカフェ") ||
                item.title.includes("映えるカフェ")))) && (
              <div className={s.instagramDirectory}>
                {cafeInstagram.map(([name, account, url]) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer">
                    <span>{name}</span>
                    <small>{account}</small>
                    <b aria-hidden="true">↗</b>
                  </a>
                ))}
              </div>
            )}

          {(item.hotelDinnerOptions || item.hotelOptions) && (
            <>
              <div className={`${s.instagramDirectory} ${s.mapDirectory}`}>
                {hotelDinnerOptions.map(([name, label, url]) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer">
                    <span>
                      <strong>{name}</strong>
                      <small>{label}</small>
                    </span>
                    <em>{label}</em>
                  </a>
                ))}
              </div>
              <a
                href={hotelBookingUrl}
                target="_blank"
                rel="noreferrer"
                className={s.hotelBookingLink}
              >
                {t.customPlans.hotelBooking}
              </a>
            </>
          )}

          {item.category === "dinner" && !item.hotelDinnerOptions && (
              <div className={`${s.instagramDirectory} ${s.mapDirectory}`}>
                {dinnerInstagram.map(([name, account, url, phone]) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer">
                    <span>
                      <strong>{name}</strong>
                      <small>{phone}</small>
                    </span>
                    <em>{account}</em>
                  </a>
                ))}
              </div>
            )}

          {item.category === "lunch" && (
            <div className={`${s.instagramDirectory} ${s.mapDirectory}`}>
              {lunchInstagram.map(([name, , url, phone]) => (
                <a key={name} href={url} target="_blank" rel="noreferrer">
                  <span>
                    <strong>{name}</strong>
                    <small>{phone}</small>
                  </span>
                  <em>Google Map</em>
                </a>
              ))}
            </div>
          )}

          {schedule.length > 0 && (
            <div className={s.tourSchedule}>
              {schedule.map(([time, label]) => (
                <div key={`${item.id}-${time}-${label}`}>
                  <span>{time}</span>
                  <p>{label}</p>
                </div>
              ))}
            </div>
          )}

          {item.bookingUrl && (
            <div className={s.cardActions}>
              <a
                href={item.bookingUrl}
                target="_blank"
                rel="noreferrer"
                className={s.routeButton}
              >
                {item.bookingLabel || t.bookingDefault}
              </a>
            </div>
          )}

          {item.detailUrl && (
            <div className={s.cardActions}>
              <a href={item.detailUrl} className={s.routeButton}>
                {item.detailLabel || t.detailDefault}
              </a>
            </div>
          )}

          {!isTour &&
            ((item.instagramUrl && !hasInstagramEmbed) ||
              item.phoneUrl ||
              item.websiteUrl ||
              hasSpecificMapLocation(item)) && (
            <div className={s.cardActions}>
              {item.instagramUrl && !hasInstagramEmbed && (
                <a href={item.instagramUrl} target="_blank" rel="noreferrer">
                  Instagram
                </a>
              )}
              {item.phoneUrl && <a href={item.phoneUrl}>0980-79-9951</a>}
              {item.websiteUrl && (
                <a href={item.websiteUrl} target="_blank" rel="noreferrer">
                  {item.websiteLabelKey
                    ? t[item.websiteLabelKey]
                    : item.websiteLabel || t.officialSite}
                </a>
              )}
              {hasSpecificMapLocation(item) && (
                <a
                  href={mapSearchUrl(item.mapQuery)}
                  target="_blank"
                  rel="noreferrer"
                  className={s.mapButton}
                >
                  {t.map}
                </a>
              )}
              {hasSpecificMapLocation(previousItem) &&
                hasSpecificMapLocation(item) && (
                <a
                  href={mapDirectionsUrl([previousItem, item])}
                  target="_blank"
                  rel="noreferrer"
                  className={s.routeButton}
                >
                  {t.routeShort}
                </a>
              )}
            </div>
            )}
        </div>
      </div>
    </article>
  );
};

const ItineraryPlannerPage = () => {
  const { lang } = useLanguage();
  const t = text[lang] || text.JP;
  const weatherCopy = ({
    JP: { start: "旅行開始日", linked: "Ocean Guideの予報と連携", rain: "降水", wind: "最大風速", wave: "最大波高", open: "詳しい海況を見る", unavailable: "予報待ち" },
    EN: { start: "Trip start date", linked: "Linked with Ocean Guide forecast", rain: "Rain", wind: "Max wind", wave: "Max waves", open: "View detailed ocean conditions", unavailable: "Forecast pending" },
    中文: { start: "旅行开始日期", linked: "连接Ocean Guide预报", rain: "降水", wind: "最大风速", wave: "最大浪高", open: "查看详细海况", unavailable: "等待预报" },
    한국어: { start: "여행 시작일", linked: "Ocean Guide 예보 연동", rain: "강수", wind: "최대 풍속", wave: "최대 파고", open: "자세한 해황 보기", unavailable: "예보 대기" },
  })[lang] || {
    start: "旅行開始日", linked: "Ocean Guideの予報と連携", rain: "降水", wind: "最大風速", wave: "最大波高", open: "詳しい海況を見る", unavailable: "予報待ち",
  };
  const [nights, setNights] = useState(2);
  const [startDate, setStartDate] = useState(() => localDateKey());
  const [oceanCache, setOceanCache] = useState(null);
  const [inputMode, setInputMode] = useState("mood");
  const [selectedInterests, setSelectedInterests] = useState(defaultInterests);
  const [selectedMood, setSelectedMood] = useState("healing");
  const [generatedConfig, setGeneratedConfig] = useState({
    nights: 2,
    interests: moodInterestMap.healing,
    inputMode: "mood",
    mood: "healing",
    breakfastIndex: 0,
    startDate: localDateKey(),
  });
  const [selectedDay, setSelectedDay] = useState(1);
  const [generatedMessage, setGeneratedMessage] = useState("");
  const resultRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL || ""}/ocean-data-cache.json`, { cache: "no-store" })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("forecast")))
      .then(async (data) => {
        const includesToday = data?.weather?.daily?.time?.includes(localDateKey());
        return includesToday ? data : fetchLiveTripOceanData();
      })
      .then((data) => { if (active) setOceanCache(data); })
      .catch(async () => {
        try {
          const data = await fetchLiveTripOceanData();
          if (active) setOceanCache(data);
        } catch {
          if (active) setOceanCache(null);
        }
      });
    return () => { active = false; };
  }, []);

  const days = useMemo(
    () =>
      buildItinerary({
        nights: generatedConfig.nights,
        interests: generatedConfig.interests,
        lang,
        breakfastIndex: generatedConfig.breakfastIndex,
        inputMode: generatedConfig.inputMode,
        mood: generatedConfig.mood,
      }),
    [generatedConfig, lang],
  );
  const activeDay = days.find((day) => day.day === selectedDay) || days[0];
  const tripForecast = useMemo(
    () => buildTripForecast(oceanCache, generatedConfig.startDate, days.length),
    [oceanCache, generatedConfig.startDate, days.length],
  );
  const activeForecast = tripForecast[activeDay.day - 1];

  const toggleInterest = (key) => {
    setSelectedInterests((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  };

  const generatePlan = () => {
    const interestsForPlan =
      inputMode === "mood"
        ? moodInterestMap[selectedMood] || moodInterestMap.healing
        : selectedInterests;
    const nextConfig = {
      nights,
      interests: interestsForPlan,
      inputMode,
      mood: selectedMood,
      breakfastIndex: Math.floor(Math.random() * breakfastOptions.length),
      startDate,
    };

    setGeneratedConfig(nextConfig);
    setSelectedDay(1);
    setGeneratedMessage(t.generatedDone);

    window.setTimeout(() => {
      resultRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);

    window.setTimeout(() => setGeneratedMessage(""), 1800);
  };

  return (
    <>
      <Header logoColor="dark" />

      <main className={s.root}>
        <section className={s.hero}>
          <img
            src={img("v1784682749/DJI_20260721090315_0012_D_ndyr5p.webp")}
            alt=""
            className={s.heroImage}
          />
          <div className={s.heroShade} />

          <div className={s.heroInner}>
            <div className={s.heroCopy}>
              <p className={s.kicker}>{t.kicker}</p>
              <h1>{t.title}</h1>
              <p>{t.lead}</p>
            </div>

            <div className={s.generatorCard}>
              <div className={s.fieldGroup}>
                <span>{weatherCopy.start}</span>
                <input
                  type="date"
                  value={startDate}
                  min={localDateKey()}
                  onChange={(event) => setStartDate(event.target.value || localDateKey())}
                  className={s.dateInput}
                />
              </div>

              <div className={s.fieldGroup}>
                <span>{t.nights}</span>
                <div className={s.nightButtons}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={nights === value ? s.selectedOption : ""}
                      onClick={() => setNights(value)}
                    >
                      {t.nightsOptions[value]}
                    </button>
                  ))}
                </div>
              </div>

              <div className={s.fieldGroup}>
                <span>{t.inputModeTitle}</span>
                <div className={s.modeToggle}>
                  {["mood", "interests"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={inputMode === mode ? s.selectedOption : ""}
                      onClick={() => setInputMode(mode)}
                    >
                      {t.inputModes[mode]}
                    </button>
                  ))}
                </div>
              </div>

              {inputMode === "interests" ? (
                <div className={s.fieldGroup}>
                  <span>{t.interests}</span>
                  <div className={s.interestGrid}>
                    {planOrder.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={
                          selectedInterests.includes(key)
                            ? s.selectedOption
                            : ""
                        }
                        onClick={() => toggleInterest(key)}
                      >
                        {t.interestLabels[key]}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={s.fieldGroup}>
                  <span>{t.moods}</span>
                  <div className={s.interestGrid}>
                    {moodOrder.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={selectedMood === key ? s.selectedOption : ""}
                        onClick={() => setSelectedMood(key)}
                      >
                        {t.moodLabels[key]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                className={s.generateButton}
                onClick={generatePlan}
              >
                {t.generate}
              </button>
            </div>
          </div>
        </section>

        <section className={s.contentWrap} ref={resultRef}>
          <div className={s.sectionHeader}>
            <div>
              <p>{t.result}</p>
              <h2>{t.nightsOptions[generatedConfig.nights]}</h2>
              <span className={s.oceanLinkedBadge}>☀︎ {weatherCopy.linked}</span>
            </div>
            <div className={s.resultHeaderActions}>
              <span>{generatedMessage || t.helper}</span>
              <a
                href={mapDirectionsUrl(days.flatMap((day) => day.items))}
                target="_blank"
                rel="noreferrer"
                className={s.allRouteTopButton}
              >
                <span aria-hidden="true">⌖</span>
                {t.allRouteText}
                <b aria-hidden="true">↗</b>
              </a>
            </div>
          </div>

          <nav className={s.dayTabs} aria-label={t.result}>
            {days.map((day) => (
              <button
                key={day.day}
                type="button"
                className={day.day === activeDay.day ? s.activeTab : ""}
                onClick={() => setSelectedDay(day.day)}
              >
                <span>
                  {t.day} {day.day}
                </span>
                <small>{day.title}</small>
                <span className={s.dayWeather}>
                  {tripForecast[day.day - 1]?.available
                    ? `${weatherIcon(tripForecast[day.day - 1].code)} ${tripForecast[day.day - 1].high}° / ${tripForecast[day.day - 1].low}°`
                    : weatherCopy.unavailable}
                </span>
              </button>
            ))}
          </nav>

          <section className={s.contentGrid}>
            <div className={s.dayPanel}>
              <div className={s.dayHeader}>
                <div>
                  <p className={s.dayCount}>
                    <span>{activeDay.day}</span> / {days.length}
                  </p>
                  <h2>{activeDay.title}</h2>
                  <p>{activeDay.theme}</p>
                </div>
              </div>

              <div className={`${s.weatherAdvice} ${s[activeForecast?.level || "pending"]}`}>
                <div className={s.weatherAdviceHead}>
                  <span>{activeForecast?.available ? weatherIcon(activeForecast.code) : "◌"}</span>
                  <div>
                    <small>{activeForecast?.date || addLocalDays(generatedConfig.startDate, activeDay.day - 1)}</small>
                    <strong>{activeForecast?.available ? `${activeForecast.high}℃ / ${activeForecast.low}℃` : weatherCopy.unavailable}</strong>
                  </div>
                </div>
                {activeForecast?.available && (
                  <div className={s.weatherMetrics}>
                    <span>{weatherCopy.rain} <b>{activeForecast.rain}%</b></span>
                    <span>{weatherCopy.wind} <b>{activeForecast.wind.toFixed(1)}m/s</b></span>
                    <span>{weatherCopy.wave} <b>{activeForecast.wave.toFixed(1)}m</b></span>
                  </div>
                )}
                <p>{tripWeatherText(activeForecast, lang)}</p>
                <a href="/ocean-guide">{weatherCopy.open}<span aria-hidden="true">→</span></a>
              </div>

              {activeDay.items.some(hasSpecificMapLocation) && (
                <a
                  href={mapDirectionsUrl(activeDay.items)}
                  target="_blank"
                  rel="noreferrer"
                  className={s.routeCta}
                >
                  {t.route}
                </a>
              )}

              <div className={s.timeline}>
                {activeDay.items.map((item, index) => (
                  <TimelineCard
                    key={item.id}
                    item={item}
                    index={index}
                    previousItem={activeDay.items[index - 1]}
                    t={t}
                    lang={lang}
                  />
                ))}
              </div>
            </div>

            <aside className={s.sidePanel}>
              <div className={s.mapPreview}>
                <iframe
                  className={s.mapEmbed}
                  src="https://www.google.com/maps?q=%E5%AE%AE%E5%8F%A4%E5%B3%B6&z=10&output=embed"
                  title={t.mapPreviewTitle}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <span className={s.mapLabel}>MIYAKOJIMA MAP</span>
              </div>

            </aside>
          </section>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ItineraryPlannerPage;
